import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { Icon, IconName } from '../components/ui/Icon';
import { DiscountStanceDot } from '../components/ui/DiscountStanceChip';
import { HowItWorksLink } from '../components/ui/HowItWorksLink';
import { AssetMappingPicker } from '../components/partnerships/AssetMappingPicker';
import PlacementsPanel from '../components/partnerships/PlacementsPanel';
import AiTextSuggest from '../components/partnerships/AiTextSuggest';
import TranslationsPanel from '../components/partnerships/TranslationsPanel';
import axiosClient from '../utils/axiosInstance';
import RichTextEditor from '../components/ui/RichTextEditor';
import { useToast } from '../components/ui/Toast';
import { CommitInput } from '../components/ui/CommitInput';
import { pastedPicture, readPicture } from '../lib/api/placementMap';
import { useBrands } from '../lib/api/brands';
import { useAssets, useUpdateAsset } from '../lib/api/assets';
import { useCampaign } from '../lib/api/campaigns';
import {
  METRIC_FIELDS_BY_CATEGORY, computeRates, hasAnyMetric,
} from '../lib/metrics';
import { CampaignAsset } from '../lib/types';
import { fmtShort } from '../lib/dates';
import { useTeam } from '../lib/api/tasks';
import {
  Partnership,
  PartnershipAddon,
  PartnershipFeature,
  PartnershipPack,
  PartnershipPreview,
  PartnershipPlacementRow,
  PartnershipResponse,
  PartnershipResponseStatus,
  PartnershipStat,
  useAddBrandToTrack,
  useAddCampaignBrands,
  useChangeBooking,
  usePartnershipPlacements,
  useContactShortlist,
  useCreatePartnership,
  useDeletePartnership,
  useDeletePartnershipResponse,
  useGenerateDraftPlacements,
  GeneratedPlacementsSummary,
  usePartnership,
  usePartnershipCatalog,
  usePartnershipResponses,
  usePublishPartnership,
  useUpdatePartnership,
  useUpdatePartnershipResponse,
  useGenerateInvoice,
  useBindOdooSupplier,
  useOdooSupplierSearch,
  OdooSupplier,
  Settlement,
  SETTLEMENT_LABELS,
} from '../lib/api/partnerships';

/* ---------- generic editable list ---------- */

interface ListEditorProps<T> {
  items: T[];
  onChange: (next: T[]) => void;
  blank: () => T;
  renderItem: (item: T, idx: number, update: (patch: Partial<T>) => void) => React.ReactNode;
  addLabel?: string;
  emptyLabel?: string;
}

export function ListEditor<T>({
  items,
  onChange,
  blank,
  renderItem,
  addLabel = 'Add row',
  emptyLabel = 'No rows yet.',
}: ListEditorProps<T>) {
  const update = (idx: number, patch: Partial<T>) => {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, blank()]);
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.length === 0 && (
        <div style={{ color: 'var(--ink-4)', fontSize: 12, fontStyle: 'italic' }}>{emptyLabel}</div>
      )}
      {items.map((item, idx) => (
        <div
          key={idx}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: 12,
            background: 'var(--bg-elevated)',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>{renderItem(item, idx, (p) => update(idx, p))}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button type="button" className="link" onClick={() => move(idx, -1)} aria-label="Move up" disabled={idx === 0}>↑</button>
            <button type="button" className="link" onClick={() => move(idx, 1)} aria-label="Move down" disabled={idx === items.length - 1}>↓</button>
            <button
              type="button"
              className="link"
              onClick={() => remove(idx)}
              aria-label="Remove"
              style={{ color: 'var(--warn)' }}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
      <Button variant="ghost" onClick={add}>
        <Icon name="plus" size={12} /> {addLabel}
      </Button>
    </div>
  );
}

/* ---------- small helpers ---------- */

export const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 500,
  color: 'var(--ink-3)',
  marginBottom: 5,
};

/* ---------- Page tab layout, in the Black Friday pages' style ---------- */

const PP_STYLES = `
.pp .pp-settings { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; padding: 14px; margin-bottom: 22px; background: #F3EFE7; border: 1px solid #E6DFD2; border-radius: var(--r-lg); }
.pp .pp-settings .tile { background: rgba(255,255,255,0.75); border-radius: var(--r-md); padding: 12px 14px; min-width: 0; display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
.pp .pp-settings .tile.wide { grid-column: span 2; }
@media (max-width: 760px) { .pp .pp-settings .tile.wide { grid-column: auto; } }
.pp .pp-settings .k { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; }
.pp .hint { font-size: 11.5px; color: var(--ink-4); line-height: 1.45; }
.pp .check { display: flex; gap: 8px; align-items: center; font-size: 13px; cursor: pointer; color: var(--ink-2); }
.pp .seg { display: inline-flex; gap: 2px; padding: 2px; background: var(--bg-subtle); border: 1px solid var(--border); border-radius: 999px; }
.pp .seg button { border: 0; background: transparent; font: inherit; font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 999px; color: var(--ink-3); cursor: pointer; }
.pp .seg button[aria-pressed="true"] { background: var(--bg-elevated); color: var(--ink); box-shadow: var(--shadow-sm); }
.pp .seg button.yes[aria-pressed="true"] { background: #E3F4E8; color: #1F6B3A; }
.pp .seg button.no[aria-pressed="true"] { background: #FDE8E6; color: #9A2B22; }
.pp .chips { display: flex; flex-wrap: wrap; gap: 6px; }
.pp .chips button { display: inline-flex; align-items: center; gap: 5px; border: 1px solid var(--border); background: var(--bg-elevated); font: inherit; font-size: 12px; font-weight: 600; padding: 4px 11px; border-radius: 999px; color: var(--ink-3); cursor: pointer; }
.pp .chips button[aria-pressed="true"] { background: var(--ink); border-color: var(--ink); color: #fff; }
.pp .pp-sec { --sec: var(--ink-4); margin-bottom: 18px; }
.pp .pp-sec .section-head { margin-bottom: 8px; }
.pp .pp-sec-title { display: flex; align-items: center; gap: 8px; color: var(--ink-3); background: transparent; border: 0; padding: 0; cursor: pointer; font: inherit; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; text-align: left; }
.pp .pp-sec-title:hover { color: var(--ink); }
.pp .pp-sec-title .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--sec); flex: none; }
.pp .pp-sec-title svg { color: var(--sec); }
.pp .pp-sec-title .sum { color: var(--ink-5); font-weight: 500; text-transform: none; letter-spacing: 0; font-size: 12px; margin-left: 2px; }
.pp .pp-sec-title .chev { color: var(--ink-5); display: inline-flex; margin-left: 2px; }
.pp .pp-sec-card { border-left: 3px solid var(--sec); padding: 14px 16px 16px; }
.pp .sec-sub { font-size: 12px; color: var(--ink-4); margin: -2px 0 12px; line-height: 1.5; }
.pp .grp { padding: 2px 0 8px; }
.pp .grp + .grp { border-top: 1px solid var(--border-soft); padding-top: 16px; margin-top: 8px; }
.pp .grp-title { font-size: 13px; font-weight: 600; color: var(--ink-2); margin-bottom: 4px; letter-spacing: -0.005em; }
.pp .grp-title + :not(.sec-sub) { margin-top: 8px; }
`;

type PPSectionKey = 'text' | 'offer' | 'pictures' | 'info' | 'lang' | 'emails';
const PP_SECTIONS: Record<PPSectionKey, { label: string; icon: IconName; color: string }> = {
  text: { label: 'Page text', icon: 'globe', color: 'var(--sitewide)' },
  offer: { label: 'What brands can buy', icon: 'tag', color: 'var(--seasonal)' },
  pictures: { label: 'Pictures', icon: 'image', color: 'var(--brand)' },
  info: { label: 'Good to know', icon: 'helpCircle', color: 'var(--accent-3)' },
  lang: { label: 'Translations (NL · FR)', icon: 'globe', color: 'var(--sitewide)' },
  emails: { label: 'Emails to brands', icon: 'mail', color: 'var(--accent-5)' },
};

/** A section of the Page tab: uppercase title above the card (dot + icon,
 *  one-line summary when folded), coloured left bar — same pattern as the
 *  Black Friday brand page. Open/closed is remembered per section. */
const PPSection = ({ k, summary, children }: { k: PPSectionKey; summary?: string; children: React.ReactNode }) => {
  const s = PP_SECTIONS[k];
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(`pp-sec-${k}`) === 'open'; } catch { return false; }
  });
  const toggle = () => {
    setOpen((o) => {
      try { localStorage.setItem(`pp-sec-${k}`, o ? 'closed' : 'open'); } catch { /* private mode */ }
      return !o;
    });
  };
  return (
    <div className="pp-sec" style={{ '--sec': s.color } as React.CSSProperties}>
      <div className="section-head">
        <button type="button" className="section-title pp-sec-title" onClick={toggle} aria-expanded={open}>
          <span className="dot" />
          <Icon name={s.icon} size={13} />
          {s.label}
          {!open && summary && <span className="sum">· {summary}</span>}
          <span className="chev"><Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} /></span>
        </button>
      </div>
      {open && <div className="card pp-sec-card">{children}</div>}
    </div>
  );
};

/** A sub-group inside a section: small heading (+ optional line), then fields. */
const Group = ({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) => (
  <div className="grp">
    <div className="grp-title">{title}</div>
    {sub && <div className="sec-sub" style={{ marginTop: 0 }}>{sub}</div>}
    {children}
  </div>
);

/** Two or three choices as pill buttons (the Black Friday yes/no style). */
const Seg = <T extends string>({ value, options, onChange }: {
  value: T; options: { v: T; l: string; cls?: string }[]; onChange: (v: T) => void;
}) => (
  <span className="seg">
    {options.map((o) => (
      <button key={o.v} type="button" className={o.cls} aria-pressed={value === o.v} onClick={() => onChange(o.v)}>
        {o.l}
      </button>
    ))}
  </span>
);

/* The "main" field of an item (feature / pack / add-on name) — made to
   stand out so the setup page reads as a clear structure. */
export const primaryInputStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
};

/** Small uppercase divider for grouping fields inside a complex editor
 *  card (e.g. each pack has Highlighting / Availability / Features
 *  groups). Visual breathing room without a heavy heading. */
export const SubsectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div style={{
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em',
    fontWeight: 600, color: 'var(--ink-4)',
    borderTop: '1px solid var(--border-soft)',
    paddingTop: 10, marginTop: 4,
  }}>
    {children}
  </div>
);



/* ---------- image compression (mirror of the asset proof-shot path) ---------- */

export const compressImage = (file: File, maxDim = 1400, quality = 0.85): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });

/* ---------- "add a standard one" picker ---------- */

export function AddFromCatalog<T extends { id: string }>({
  catalog,
  present,
  label,
  describe,
  onAdd,
}: {
  catalog: T[];
  present: { id: string }[];
  label: string;
  describe: (item: T) => string;
  onAdd: (item: T) => void;
}) {
  const presentIds = new Set(present.map((p) => p.id));
  const available = catalog.filter((c) => !presentIds.has(c.id));
  if (available.length === 0) return null;
  return (
    <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
      <select
        className="input"
        value=""
        onChange={(e) => {
          const item = available.find((a) => a.id === e.target.value);
          if (item) onAdd(JSON.parse(JSON.stringify(item)) as T);
          e.target.value = '';
        }}
        style={{ maxWidth: 360 }}
      >
        <option value="">{label}</option>
        {available.map((a) => (
          <option key={a.id} value={a.id}>{describe(a)}</option>
        ))}
      </select>
    </div>
  );
}


/* ---------- partner tracking table ---------- */

/* ---------- per-column filter (Notion-style) ---------- */

interface ColumnFilterOption {
  value: string;
  label: string;
}

const ColumnFilter = ({
  options,
  selected,
  onChange,
  search,
  onSearchChange,
  align = 'left',
}: {
  options?: ColumnFilterOption[];
  selected?: Set<string>;
  onChange?: (next: Set<string>) => void;
  /** When set, the popover shows a text input instead of (or in addition to) the checkbox list. */
  search?: string;
  onSearchChange?: (next: string) => void;
  align?: 'left' | 'right';
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active =
    (selected && selected.size > 0) || (typeof search === 'string' && search.trim().length > 0);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value: string) => {
    if (!selected || !onChange) return;
    const next = new Set(selected);
    if (next.has(value)) next.delete(value); else next.add(value);
    onChange(next);
  };

  const clear = () => {
    if (onChange) onChange(new Set());
    if (onSearchChange) onSearchChange('');
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={active ? 'Filtered — click to edit' : 'Filter'}
        style={{
          background: active ? 'var(--ink)' : 'transparent',
          color: active ? 'var(--paper)' : 'var(--ink-4)',
          border: 'none',
          padding: '2px 5px',
          marginLeft: 4,
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: 10,
          lineHeight: 1,
        }}
        aria-label="Filter column"
      >
        ▾
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            [align]: 0,
            zIndex: 20,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            padding: 10,
            minWidth: 200,
            maxHeight: 320,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            textTransform: 'none',
            letterSpacing: 'normal',
          }}
        >
          {typeof search === 'string' && onSearchChange && (
            <input
              className="input"
              placeholder="Search…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
              style={{ fontSize: 12 }}
            />
          )}
          {options && selected && onChange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {options.map((opt) => {
                const on = selected.has(opt.value);
                return (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex', gap: 8, alignItems: 'center',
                      padding: '4px 6px', cursor: 'pointer',
                      fontSize: 13, color: 'var(--ink)',
                      fontWeight: 400,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(opt.value)}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
          )}
          {active && (
            <button
              type="button"
              onClick={clear}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                padding: '4px 8px', cursor: 'pointer', fontSize: 12,
                color: 'var(--ink-4)',
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </span>
  );
};


const STATUS_OPTIONS: { value: PartnershipResponseStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'sent-reminder', label: 'Sent reminder' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'replied', label: 'Replied' },
  { value: 'confirmed', label: 'Signed up' },
  { value: 'auto-included', label: 'Auto-included' },
  { value: 'declined', label: 'Declined' },
  { value: 'excluded', label: 'Excluded' },
];

/** Booking pages (no packs, final sign-up): six statuses, in Doreen's
 *  words (2026-10-22). Same stored values; follow-up / excluded /
 *  auto-included are folded in by partnerships/0061. */
const BOOKING_STATUS_OPTIONS: { value: PartnershipResponseStatus; label: string }[] = [
  { value: 'pending', label: 'To contact' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'sent-reminder', label: 'Reminder sent' },
  { value: 'replied', label: 'In talks' },
  { value: 'confirmed', label: 'Signed up' },
  { value: 'declined', label: 'Not joining' },
];

const STATUS_COLORS: Record<PartnershipResponseStatus, { bg: string; fg: string }> = {
  pending:         { bg: 'var(--bg-subtle)',  fg: 'var(--ink-4)' },
  contacted:       { bg: '#fff7e0',           fg: '#a35a00' },
  'sent-reminder': { bg: '#efe6fa',           fg: '#5e3a9e' },
  'follow-up':     { bg: '#fde7d3',           fg: '#a04d0a' },
  replied:         { bg: '#e6f0ff',           fg: '#1d4ed8' },
  confirmed:       { bg: '#e7f6ec',           fg: '#0d7a36' },
  'auto-included': { bg: '#eef1f7',           fg: '#475569' },
  declined:        { bg: '#fbe7e7',           fg: '#9a1d1d' },
  excluded:        { bg: '#ececec',           fg: '#6b7280' },
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

/** "27 Oct, 14:32" — when a brand signed up on the booking page. */
const fmtSignedUp = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Booked slots of one brand, changeable by the team (the brand called):
 *  × removes a slot (it's free again), "+ Add slot" books a free one at
 *  today's price (Doreen, 2026-10-27). */
const BookedCell = ({ row, campaignId, placements }: {
  row: PartnershipResponse;
  campaignId: string;
  placements?: PartnershipPlacementRow[];
}) => {
  const change = useChangeBooking(campaignId);
  const update = useUpdatePartnershipResponse(campaignId);
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [proposing, setProposing] = useState(false);
  const booked = row.booked ?? [];
  const mine = new Set(booked.map((b) => b.id));
  const proposed = row.proposed_slots ?? [];
  const slots = (placements ?? [])
    .filter((p) => p.on_sale && !p.needs_sold_as)
    .flatMap((p) => p.slots
      .filter((sl) => sl.publish_date)
      .map((sl) => ({
        id: sl.id,
        full: sl.taken >= sl.brand_spots,
        price: sl.price != null && Number(sl.price) > 0 ? Number(sl.price) : 0,
        label: `${p.display_name || p.asset_type} · ${fmtShort(sl.publish_date!)}`,
      })));
  const withPrice = (s: { label: string; price: number }) => `${s.label}${s.price ? ` · ${fmtMoney(s.price)}` : ''}`;
  const free = slots.filter((s) => !mine.has(s.id) && !s.full).map((s) => ({ id: s.id, label: withPrice(s) }));
  const byId = new Map(slots.map((s) => [s.id, s]));
  const proposedFree = free.filter((f) => !proposed.includes(f.id));
  const proposedTotal = proposed.reduce((n, id) => n + (byId.get(id)?.price ?? 0), 0);
  const setProposed = (ids: number[]) =>
    update.mutate({ id: row.id, patch: { proposed_slots: ids } }, { onError: fail });
  // Proposals only make sense before the brand signs up (sign-up clears them).
  const canPropose = !(row.status === 'confirmed' && booked.length);
  const fail = (e: unknown) => {
    const d = (e as { response?: { data?: { detail?: string } } })?.response?.data;
    toast.push(d?.detail || 'Could not change the booking.');
  };
  const remove = (id: number, name: string) => {
    if (!window.confirm(`Remove ${name} from ${row.brand.name}'s booking? The slot is free again and the total goes down.`)) return;
    change.mutate({ rowId: row.id, remove: id }, { onError: fail });
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {booked.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {booked.map((b) => {
            const label = `${b.name}${b.date ? ` · ${fmtShort(b.date)}` : ''}`;
            return (
              <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 8px', borderRadius: 999, background: '#e6f4ea', color: '#1e7a3c', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {label}{b.price != null && Number(b.price) > 0 ? ` · ${fmtMoney(Number(b.price))}` : ''}
                <button
                  type="button"
                  title="Remove this slot from the booking"
                  disabled={change.isPending}
                  onClick={() => remove(b.id, label)}
                  style={{ border: 0, background: 'transparent', color: '#1e7a3c', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 4px', opacity: 0.7 }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : !proposed.length ? <span style={{ color: 'var(--ink-5)' }}>—</span> : null}
      {canPropose && proposed.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {proposed.map((id) => {
            const s = byId.get(id);
            const label = s ? withPrice(s) : 'Slot no longer on sale';
            return (
              <span key={id}
                title={s?.full ? 'Proposed, but another brand booked it in the meantime' : 'Proposed — ticked in advance on the brand’s booking link, not reserved'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 4px 1px 8px', borderRadius: 999, border: '1px dashed var(--ink-4)', color: 'var(--ink-3)', whiteSpace: 'nowrap', textDecoration: s?.full || !s ? 'line-through' : 'none' }}>
                {label}
                <button type="button" title="Take out of the proposal" disabled={update.isPending}
                  onClick={() => setProposed(proposed.filter((x) => x !== id))}
                  style={{ border: 0, background: 'transparent', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 4px' }}>
                  ×
                </button>
              </span>
            );
          })}
          <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>Proposed · {fmtMoney(proposedTotal)}</span>
        </div>
      )}
      {proposing ? (
        <select
          className="input"
          autoFocus
          value=""
          disabled={update.isPending}
          onBlur={() => setProposing(false)}
          onChange={(e) => {
            const id = Number(e.target.value);
            if (id) setProposed([...proposed, id]);
          }}
          style={{ maxWidth: 320, fontSize: 12 }}
        >
          <option value="">{proposedFree.length ? 'Propose a free slot…' : 'No free slots left'}</option>
          {proposedFree.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
      ) : null}
      {adding ? (
        <select
          className="input"
          autoFocus
          value=""
          disabled={change.isPending}
          onBlur={() => setAdding(false)}
          onChange={(e) => {
            const id = Number(e.target.value);
            if (id) change.mutate({ rowId: row.id, add: id }, { onError: fail, onSettled: () => setAdding(false) });
          }}
          style={{ maxWidth: 320, fontSize: 12 }}
        >
          <option value="">{free.length ? 'Pick a free slot…' : 'No free slots left'}</option>
          {free.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
      ) : !proposing && (
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className="link" onClick={() => setAdding(true)}
            style={{ border: 0, background: 'transparent', padding: 0, color: 'var(--ink-4)', fontSize: 12, cursor: 'pointer' }}>
            + Add slot
          </button>
          {canPropose && (
            <button type="button" className="link" onClick={() => setProposing(true)}
              title="Tick slots in advance on this brand’s booking link — it only has to review and sign up. Not reserved."
              style={{ border: 0, background: 'transparent', padding: 0, color: 'var(--ink-4)', fontSize: 12, cursor: 'pointer' }}>
              + Prepare selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const FinalPriceCell = ({
  row,
  apply,
}: {
  row: PartnershipResponse;
  apply: (id: number, patch: Partial<PartnershipResponse>) => void;
}) => {
  const isAuto = row.final_price == null;
  const [draft, setDraft] = useState<string>(row.final_price ?? '');
  // Keep local state in sync when the server-side row changes
  // (e.g. another field updated and the cache refreshed).
  useEffect(() => {
    setDraft(row.final_price ?? '');
  }, [row.final_price]);

  const commit = (next: string) => {
    const trimmed = next.trim();
    // Empty input → revert to auto.
    if (trimmed === '' && row.final_price != null) {
      apply(row.id, { final_price: null });
      return;
    }
    if (trimmed !== '' && trimmed !== (row.final_price ?? '')) {
      // Backend expects a decimal string ("1500.00") — let it normalise.
      apply(row.id, { final_price: trimmed });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        className="input"
        type="number"
        step="0.01"
        min="0"
        value={draft}
        placeholder={isAuto ? String(row.effective_price ?? 0) : ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        style={{
          width: '100%',
          color: isAuto ? 'var(--ink-4)' : 'var(--ink)',
          fontStyle: isAuto ? 'italic' : 'normal',
        }}
        title={isAuto ? 'Auto — taken from pack + selected add-ons. Type to override.' : 'Final price negotiated with this brand.'}
      />
      {isAuto && (
        <span style={{
          fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--ink-4)', background: 'var(--bg-subtle)',
          borderRadius: 'var(--r-pill)', padding: '1px 6px', fontWeight: 600,
        }}>
          Auto
        </span>
      )}
    </div>
  );
};

/* ---------- email helpers ---------- */

/** Comma-separated list of every email on a brand's contact rows
 *  (primary, secondary, tertiary). Empties dropped, duplicates
 *  collapsed, all whitespace trimmed. */
const collectBrandEmails = (
  brand: PartnershipResponse['brand'],
): string => {
  const candidates = [
    brand.contact_email,
    brand.contact_email_2,
    brand.contact_email_3,
  ];
  const cleaned: string[] = [];
  const seen = new Set<string>();
  for (const raw of candidates) {
    const t = (raw || '').trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    cleaned.push(t);
  }
  return cleaned.join(', ');
};

/** Comma-separated list of every team-contact email on the partnership,
 *  excluding any address already in the To line. Used as the CC on
 *  both outreach and confirmation emails so the campaign team stays
 *  in the loop when a brand replies. */
const collectPartnershipCc = (
  partnership: Partnership,
  toLine: string,
): string => {
  const toSet = new Set(
    toLine.split(',').map((e) => e.trim().toLowerCase()).filter(Boolean),
  );
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of partnership.contacts ?? []) {
    const e = (c.email || '').trim();
    if (!e) continue;
    const k = e.toLowerCase();
    if (toSet.has(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out.join(', ');
};

/** Small "✉" button next to a brand on the Partners row. Opens a
 *  pre-filled mailto: with the brand's contact email + a body that
 *  carries the brand's UNIQUE opt-in link (?token=).
 *  The body is built from the partnership's editable
 *  `outreach_email_template` with per-brand placeholder substitution. */
const renderEmailBody = (
  template: string,
  values: Record<string, string>,
): string =>
  // Only keys present in `values` are filled; anything else stays as typed.
  template.replace(/\{([a-z_]+)\}/g, (m, key) => (key in values ? values[key] : m));

const LONG_MONTH = (m: number): string => (
  ['January', 'February', 'March', 'April', 'May', 'June',
   'July', 'August', 'September', 'October', 'November', 'December'][m] || ''
);

const fmtLongDate = (iso: string): string => {
  const d = new Date(iso);
  return `${LONG_MONTH(d.getMonth())} ${d.getDate()}`;
};

/** Second mailto button — sent once Doreen has reviewed a brand's reply
 *  and is locking it in. Same placeholder substitution as the outreach
 *  button, plus pack/addons/discount/dates so the email can echo the
 *  agreed scope back to the brand. Only meaningful from Replied onward. */
const ConfirmEmailButton = ({
  row,
  partnership,
}: {
  row: PartnershipResponse;
  partnership: Partnership;
}) => {
  const brand = row.brand;
  const to = collectBrandEmails(brand);
  if (!to) return null;
  // Only enabled once we have something to confirm — i.e. the brand has
  // replied (or is auto-included / confirmed).
  // Booking pages: only once the brand has signed up — the email thanks
  // them for their booking (Doreen, 2026-10-27).
  const canSend = partnership.uses_packs
    ? ['replied', 'confirmed', 'auto-included'].includes(row.status)
    : row.status === 'confirmed';
  if (!canSend) return null;

  const campaignName = partnership.campaign_details?.name || '';
  const firstName = (brand.contact_name || '').trim().split(/\s+/)[0] || 'team';
  const sd = partnership.campaign_details?.start_date || '';
  const ed = partnership.campaign_details?.end_date || '';
  const dates = sd && ed ? `from ${fmtLongDate(sd)} to ${fmtLongDate(ed)}` : '';
  const pack = partnership.packs.find((p) => p.id === row.selected_pack);
  const packName = pack?.name || '(no pack picked)';
  const addonLabels = (row.selected_addons ?? [])
    .map((id) => partnership.addons.find((a) => a.id === id)?.label)
    .filter(Boolean) as string[];
  const addonsLine = addonLabels.length
    ? addonLabels.map((l) => ` · Add-on: ${l}\n`).join('')
    : '';
  const discount = (row.discount || '—').trim();
  // Booking pages: what the brand booked, one line per slot, and the total.
  // Grouped per placement, each with what we need from the brand for it
  // (Robbie / Doreen, 2026-10-27), so they can reply with their material.
  const groups = new Map<string, { dates: string[]; needs: string }>();
  for (const b of row.booked ?? []) {
    const g = groups.get(b.name) ?? { dates: [], needs: b.needs ?? '' };
    if (b.date) g.dates.push(fmtLongDate(b.date));
    groups.set(b.name, g);
  }
  const slots = [...groups].map(([name, g]) =>
    ` · ${name}${g.dates.length ? ` — ${g.dates.join(', ')}` : ''}\n` +
    (g.needs ? `   What we need from you: ${g.needs}\n` : ''),
  ).join('');
  const total = row.effective_price ? `€${Number(row.effective_price).toLocaleString('en', { maximumFractionDigits: 2 })}` : '—';

  const fallbackTemplate = !partnership.uses_packs
    ? `Hello {first_name},\n\n` +
      `Thanks for booking {campaign} — {brand} is in!\n\n` +
      `Your slots, and what we need from you for each:\n{slots}\n` +
      `Total: {total}\n\n` +
      `Just reply to this email with your material and anything you'd like us to highlight. We'll confirm the deadlines shortly.\n\n` +
      `Speak soon! :)`
    :
    `Hello {first_name},\n\n` +
    `Great — {brand}'s participation in {campaign} is confirmed.\n\n` +
    `On file:\n` +
    ` · Pack: {pack}\n` +
    `{addons_line}` +
    ` · End-consumer discount: {discount}\n` +
    ` · Campaign runs {dates}\n\n` +
    `We'll be in touch with production details shortly.\n\n` +
    `Speak soon! :)`;
  const body = renderEmailBody(
    (partnership.confirmation_email_template || '').trim() || fallbackTemplate,
    {
      first_name: firstName,
      brand: brand.name,
      campaign: campaignName,
      pack: packName,
      addons_line: addonsLine,
      discount,
      dates,
      slots,
      total,
    },
  );
  const subject = `Confirmed: ${campaignName} for ${brand.name}`;
  const cc = collectPartnershipCc(partnership, to);
  const mailto =
    `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}` +
    (cc ? `&cc=${encodeURIComponent(cc)}` : '') +
    `&body=${encodeURIComponent(body)}`;
  return (
    <a
      href={mailto}
      title={`Send confirmation email to ${brand.contact_name || brand.name}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 4,
        color: '#0d7a36', textDecoration: 'none',
        background: '#e7f6ec',
      }}
    >
      <Icon name="check" size={12} />
    </a>
  );
};

/** Small copy-to-clipboard button next to the email icons — pulls the
 *  brand's unique opt-in URL so Doreen can paste it wherever she likes
 *  without having to open a draft email first. */
const CopyOptInLinkButton = ({
  slug,
  token,
  brandName,
}: {
  slug: string;
  token: string;
  brandName: string;
}) => {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/partnerships/${slug}?token=${encodeURIComponent(token)}`;
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.push(`Copied ${brandName}'s opt-in link`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.push('Could not copy — your browser blocked it');
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Copy ${brandName}'s unique opt-in link`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 4,
        color: copied ? '#0d7a36' : 'var(--ink-3)',
        background: copied ? '#e7f6ec' : 'var(--bg-subtle)',
        border: 'none', cursor: 'pointer',
      }}
    >
      <Icon name={copied ? 'check' : 'link'} size={12} />
    </button>
  );
};

const ComposeEmailButton = ({
  brand,
  token,
  partnership,
}: {
  brand: PartnershipResponse['brand'];
  token: string;
  partnership: Partnership;
}) => {
  const to = collectBrandEmails(brand);
  if (!to) {
    return (
      <span
        title="No email on file for this brand — add one on the brand page."
        style={{
          fontSize: 11, color: 'var(--ink-4)',
          background: 'var(--bg-subtle)', padding: '1px 6px',
          borderRadius: 'var(--r-pill)',
        }}
      >
        no email
      </span>
    );
  }
  const link = `${window.location.origin}/partnerships/${partnership.slug}?token=${encodeURIComponent(token)}`;
  const campaignName = partnership.campaign_details?.name || '';
  // First name = first word of contact_name, otherwise polite fallback.
  const firstName = (brand.contact_name || '').trim().split(/\s+/)[0] || 'team';
  const sd = partnership.campaign_details?.start_date || '';
  const ed = partnership.campaign_details?.end_date || '';
  const dates = sd && ed ? `from ${fmtLongDate(sd)} to ${fmtLongDate(ed)}` : '';
  const deadline = partnership.confirmation_deadline
    ? fmtLongDate(partnership.confirmation_deadline)
    : '';
  const deadlineLine = deadline
    ? ` To keep production on schedule, we'd love your confirmation by ${deadline}.`
    : '';

  const fallbackTemplate = !partnership.uses_packs
    ? `Hello {first_name},\n\n` +
      `Hope you're doing well! :)\n\n` +
      `We're getting ready for {campaign} on LampTwist, running {dates}, and we'd love to have {brand} in it.\n\n` +
      `On this page you can see where your brand can appear and book your days directly:\n{link}\n\n` +
      `Pick the placements and days you want and sign up at the bottom — first come, first served.{deadline_line}\n\n` +
      `Looking forward to it! :)`
    :
    `Hello {first_name},\n\n` +
    `Hope you're doing well! :)\n\n` +
    `We're getting ready for our next campaign on LampTwist: {campaign}, running {dates}. ` +
    `We'd love to have {brand} in it — it feels like a natural fit.\n\n` +
    `Here's the page with everything — packs, extras, how to opt in:\n{link}\n\n` +
    `Pick a pack and hit "Confirm my participation" right on the page.{deadline_line}\n\n` +
    `Looking forward to it! :)`;

  const body = renderEmailBody(
    (partnership.outreach_email_template || '').trim() || fallbackTemplate,
    {
      first_name: firstName,
      brand: brand.name,
      campaign: campaignName,
      dates,
      deadline_line: deadlineLine,
      deadline,
      link,
    },
  );
  const subject = `Join us for ${campaignName}`;
  const cc = collectPartnershipCc(partnership, to);
  const mailto =
    `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}` +
    (cc ? `&cc=${encodeURIComponent(cc)}` : '') +
    `&body=${encodeURIComponent(body)}`;
  return (
    <a
      href={mailto}
      title={`Compose opt-in email to ${brand.contact_name || brand.name} (${to})`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 4,
        color: 'var(--ink-3)', textDecoration: 'none',
        background: 'var(--bg-subtle)',
      }}
    >
      <Icon name="mail" size={12} />
    </a>
  );
};

const PartnersRevenueTotal = ({ rows, booking = false }: { rows: PartnershipResponse[]; booking?: boolean }) => {
  const confirmedRows = rows.filter((r) => r.status === 'confirmed');
  const total = confirmedRows.reduce((sum, r) => sum + (Number(r.effective_price) || 0), 0);
  if (rows.length === 0) return null;
  return (
    <div style={{
      padding: '10px 14px',
      borderTop: '1px solid var(--border)',
      background: 'var(--bg-subtle)',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 16,
      fontSize: 13,
    }}>
      <span style={{ color: 'var(--ink-4)' }}>
        {confirmedRows.length === 0
          ? (booking ? 'No brand signed up yet' : 'No confirmed brand yet')
          : `${confirmedRows.length} ${booking ? 'signed up' : 'confirmed'}`}
      </span>
      <span style={{ fontWeight: 600 }}>
        {booking ? 'Total booked' : 'Total committed'}: {fmtMoney(total)}
      </span>
    </div>
  );
};

/* ---------- Billing tab — invoice prep for confirmed partners ---------- */

const fmtEUR = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);

/** Statuses that mean "this partner owes us money for the campaign".
 *  Pending / contacted / replied / declined / excluded are excluded. */
const BILLABLE_STATUSES = new Set<PartnershipResponseStatus>([
  'confirmed', 'auto-included',
]);

interface BillingRow {
  responseId: number;
  brandId: string;
  brandName: string;
  contactName: string;
  contactEmail: string;
  packName: string;
  addonLabels: string[];
  /** Booking pages: one line per booked slot ("Product grid · 24 Nov · €150"). */
  bookedLines: string[];
  discount: string;
  amount: number;
  /** How the brand pays: invoice, credit note, extra discount, free ('' = not set). */
  settlement: Settlement;
  settlementDetail: string;
  /** Odoo supplier bound to the brand (null = needs binding first). */
  odooPartnerId: number | null;
  odooPartnerName: string;
  /** Odoo draft invoice, once generated. */
  invoiceId: number | null;
  invoiceNumber: string;
  invoiceState: string;
  invoiceUrl: string;
}

const buildBillingRows = (
  partnership: Partnership,
  rows: PartnershipResponse[],
): BillingRow[] => {
  const packById = new Map(partnership.packs.map((p) => [p.id, p]));
  const addonById = new Map(partnership.addons.map((a) => [a.id, a]));
  return rows
    .filter((r) => BILLABLE_STATUSES.has(r.status))
    // Skip free / placeholder rows — Doreen only invoices actual amounts.
    .filter((r) => (Number(r.effective_price) || 0) > 0)
    .map((r) => ({
      responseId: r.id,
      brandId: r.brand.id,
      brandName: r.brand.name,
      contactName: r.brand.contact_name || '',
      contactEmail: r.brand.contact_email || '',
      packName: packById.get(r.selected_pack)?.name || '(no pack)',
      bookedLines: (r.booked ?? []).map((b) => [
        b.name,
        b.date ? fmtShort(b.date) + (b.end_date && b.end_date !== b.date ? ` – ${fmtShort(b.end_date)}` : '') : '',
        b.price != null ? fmtEUR(Number(b.price)) : 'on request',
      ].filter(Boolean).join(' · ')),
      addonLabels: (r.selected_addons ?? [])
        .map((id) => addonById.get(id)?.label)
        .filter(Boolean) as string[],
      discount: r.discount || '',
      amount: Number(r.effective_price) || 0,
      settlement: r.settlement ?? '',
      settlementDetail: r.settlement_detail || '',
      odooPartnerId: r.brand.odoo_partner_id,
      odooPartnerName: r.brand.odoo_partner_name || '',
      invoiceId: r.odoo_invoice_id,
      invoiceNumber: r.odoo_invoice_number || '',
      invoiceState: r.odoo_invoice_state || '',
      invoiceUrl: r.odoo_invoice_url || '',
    }))
    .sort((a, b) => a.brandName.localeCompare(b.brandName));
};

/** Extract a human message from an axios error thrown by the invoice/bind
 *  endpoints (they return { detail, message }). */
const odooErrMessage = (e: unknown): string => {
  const data = (e as { response?: { data?: { message?: string; detail?: string } } })
    ?.response?.data;
  return data?.message || data?.detail || 'Something went wrong talking to Odoo.';
};

const escapeCsv = (v: string): string => {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
};

const buildCsv = (rows: BillingRow[], campaignName: string, booking: boolean): string => {
  const headers = [
    'Brand', 'Contact name', 'Contact email',
    ...(booking ? ['Booked'] : ['Pack', 'Add-ons']), 'Discount', 'Settled by', 'Amount (EUR)',
  ];
  const lines = [headers.map(escapeCsv).join(',')];
  for (const r of rows) {
    lines.push([
      r.brandName,
      r.contactName,
      r.contactEmail,
      ...(booking ? [r.bookedLines.join('; ')] : [r.packName, r.addonLabels.join(' + ')]),
      r.discount,
      settledByText(r),
      String(r.amount),
    ].map(escapeCsv).join(','));
  }
  const total = rows.reduce((s, r) => s + r.amount, 0);
  lines.push([
    `Total — ${campaignName}`, ...headers.slice(2).map(() => ''), String(total),
  ].slice(0, headers.length).map(escapeCsv).join(','));
  return lines.join('\n');
};

/** "Credit note — on the November invoice", or "" when not set. */
const settledByText = (r: BillingRow): string =>
  r.settlement
    ? [SETTLEMENT_LABELS[r.settlement], r.settlementDetail].filter(Boolean).join(' — ')
    : '';

const downloadFile = (filename: string, content: string, mime: string) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const slugifyForFile = (s: string): string =>
  s.replace(/[^\w\-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'campaign';

/** Per-row invoice action: shows a link once a draft exists, otherwise a
 *  "Generate invoice" button. When the brand isn't linked to an Odoo supplier
 *  yet, the button first opens a small live supplier search to bind one. */
const InvoiceCell = ({ row, campaignId }: { row: BillingRow; campaignId: string }) => {
  const toast = useToast();
  const generate = useGenerateInvoice(campaignId);
  const bind = useBindOdooSupplier(campaignId);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const search = useOdooSupplierSearch(picking ? query : '');
  const busy = generate.isPending || bind.isPending;

  const runGenerate = async () => {
    try {
      const res = await generate.mutateAsync(row.responseId);
      toast.push(
        res.detail === 'already-invoiced'
          ? 'Invoice already existed — opening it in Odoo.'
          : 'Draft invoice created in Odoo.',
      );
      window.open(res.invoice.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.push(odooErrMessage(e));
    }
  };

  const onGenerateClick = () => {
    if (row.odooPartnerId) runGenerate();
    else setPicking(true);
  };

  const pickSupplier = async (s: OdooSupplier) => {
    try {
      await bind.mutateAsync({ brandId: row.brandId, supplier: s });
      setPicking(false);
      setQuery('');
      toast.push(`Linked ${row.brandName} to ${s.name} in Odoo.`);
      await runGenerate();
    } catch (e) {
      toast.push(odooErrMessage(e));
    }
  };

  if (!row.invoiceId && row.settlement && row.settlement !== 'invoice') {
    return (
      <span style={{ fontSize: 12, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>
        No invoice
      </span>
    );
  }

  if (row.invoiceId) {
    const posted = row.invoiceNumber && row.invoiceNumber !== '/';
    return (
      <a
        href={row.invoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12,
          padding: '5px 9px', borderRadius: 'var(--r-md)',
          background: 'var(--seasonal-bg)', color: 'var(--seasonal)',
          textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap',
        }}
        title="Open the draft invoice in Odoo"
      >
        <Icon name="check" size={12} /> {posted ? row.invoiceNumber : 'Draft'} ↗
      </a>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={onGenerateClick}
        disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
          padding: '6px 10px', borderRadius: 'var(--r-md)',
          border: '1px solid var(--border)', background: 'var(--bg-elevated)',
          color: 'var(--ink-2)', cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
        }}
      >
        <Icon name="bolt" size={12} /> {busy ? 'Working…' : 'Generate invoice'}
      </button>

      {picking && (
        <>
          {/* click-away backdrop */}
          <div
            onClick={() => { setPicking(false); setQuery(''); }}
            style={{ position: 'fixed', inset: 0, zIndex: 40 }}
          />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41,
              width: 280, padding: 10, borderRadius: 'var(--r-lg)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-lg)', textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginBottom: 6 }}>
              Link <strong style={{ color: 'var(--ink-2)' }}>{row.brandName}</strong> to its Odoo supplier
            </div>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Odoo suppliers…"
              style={{
                width: '100%', fontSize: 12.5, padding: '7px 9px',
                borderRadius: 'var(--r-md)', border: '1px solid var(--border)',
                background: 'var(--bg)', color: 'var(--ink)', boxSizing: 'border-box',
              }}
            />
            <div style={{ maxHeight: 210, overflowY: 'auto', marginTop: 6 }}>
              {query.trim().length < 2 ? (
                <div style={{ fontSize: 11.5, color: 'var(--ink-4)', padding: '6px 2px' }}>
                  Type at least 2 letters…
                </div>
              ) : search.isLoading ? (
                <div style={{ fontSize: 11.5, color: 'var(--ink-4)', padding: '6px 2px' }}>
                  Searching…
                </div>
              ) : search.isError ? (
                <div style={{ fontSize: 11.5, color: 'var(--warn)', padding: '6px 2px' }}>
                  {odooErrMessage(search.error)}
                </div>
              ) : (search.data ?? []).length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--ink-4)', padding: '6px 2px' }}>
                  No suppliers found.
                </div>
              ) : (
                (search.data ?? []).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pickSupplier(s)}
                    disabled={busy}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '7px 8px', borderRadius: 'var(--r-md)',
                      border: 'none', background: 'transparent',
                      cursor: busy ? 'wait' : 'pointer',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                      {[s.vat, s.city, s.country_name].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** "Settled by" on Billing: how the brand pays for its booking, plus a short
 *  detail ("3% extra on PO P33785"). The detail saves when the field loses
 *  focus. Settled any other way than an invoice = no Generate-invoice button. */
const SettlementCell = ({ row, campaignId }: { row: BillingRow; campaignId: string }) => {
  const toast = useToast();
  const update = useUpdatePartnershipResponse(campaignId);
  const [detail, setDetail] = useState(row.settlementDetail);
  useEffect(() => setDetail(row.settlementDetail), [row.settlementDetail]);

  const save = (patch: { settlement?: Settlement; settlement_detail?: string }) =>
    update.mutate({ id: row.responseId, patch }, {
      onError: () => toast.push('Could not save how this brand settles'),
    });

  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 150 }}>
      <select
        className="input"
        value={row.settlement}
        onChange={(e) => save({ settlement: e.target.value as Settlement })}
        style={{ fontSize: 12, padding: '4px 6px', color: row.settlement ? 'var(--ink)' : 'var(--ink-4)' }}
      >
        {(Object.keys(SETTLEMENT_LABELS) as Settlement[]).map((k) => (
          <option key={k} value={k}>{SETTLEMENT_LABELS[k]}</option>
        ))}
      </select>
      {row.settlement && row.settlement !== 'invoice' && (
        <input
          className="input"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          onBlur={() => { if (detail.trim() !== row.settlementDetail) save({ settlement_detail: detail.trim() }); }}
          placeholder="Details, e.g. 3% on PO P33785"
          style={{ fontSize: 11.5, padding: '4px 6px' }}
        />
      )}
    </div>
  );
};

const BillingPanel = ({
  partnership,
  rows,
  campaignName,
  campaignId,
}: {
  partnership: Partnership;
  rows: PartnershipResponse[];
  campaignName: string;
  campaignId: string;
}) => {
  const billingRows = useMemo(
    () => buildBillingRows(partnership, rows),
    [partnership, rows],
  );
  // Booking pages show what was booked instead of pack + add-ons.
  const booking = !partnership.uses_packs;
  const total = useMemo(
    () => billingRows.reduce((s, r) => s + r.amount, 0),
    [billingRows],
  );

  const onDownloadCsv = () => {
    const csv = buildCsv(billingRows, campaignName, booking);
    downloadFile(
      `${slugifyForFile(campaignName)}-Billing.csv`,
      csv,
      'text/csv;charset=utf-8',
    );
  };

  /** Print the table — the browser's "Save as PDF" path is what
   *  Doreen will use. We render into a hidden iframe (popup blockers /
   *  noopener-returns-null make `window.open` unreliable across
   *  browsers), trigger print, then clean it up.  */
  const onPrint = () => {
    const rowsHtml = billingRows.map((r) => `
      <tr>
        <td>${escapeHtml(r.brandName)}</td>
        <td>${escapeHtml(r.contactName)}${r.contactEmail ? `<br><span class="muted">${escapeHtml(r.contactEmail)}</span>` : ''}</td>
        ${booking
          ? `<td>${r.bookedLines.map(escapeHtml).join('<br>') || '—'}</td>`
          : `<td>${escapeHtml(r.packName)}</td><td>${escapeHtml(r.addonLabels.join(' + ') || '—')}</td>`}
        <td>${escapeHtml(r.discount || '—')}</td>
        <td>${escapeHtml(settledByText(r) || '—')}</td>
        <td class="num">${fmtEUR(r.amount)}</td>
      </tr>
    `).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${escapeHtml(campaignName)} — Billing</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif; color: #1a1b1c; padding: 32px; margin: 0; }
        h1 { font-size: 22px; margin: 0 0 4px; font-weight: 600; }
        .sub { color: #6c7270; font-size: 12px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
        th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #e4e2dc; vertical-align: top; }
        th { color: #6c7270; font-weight: 600; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.06em; background: #f7f5f0; }
        td.num, th.num { text-align: right; white-space: nowrap; }
        .muted { color: #9a9d9c; font-size: 11px; }
        tfoot td { font-weight: 700; border-top: 2px solid #1a1b1c; border-bottom: none; padding-top: 12px; }
        @media print { body { padding: 18px; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head><body>
      <h1>${escapeHtml(campaignName)} — Billing overview</h1>
      <div class="sub">${billingRows.length} partner${billingRows.length === 1 ? '' : 's'} to invoice · generated ${new Date().toLocaleDateString('en-GB')}</div>
      <table>
        <thead>
          <tr>
            <th>Brand</th><th>Contact</th>${booking ? '<th>Booked</th>' : '<th>Pack</th><th>Add-ons</th>'}<th>Discount</th><th>Settled by</th><th class="num">Amount</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot>
          <tr><td colspan="${booking ? 5 : 6}">Total</td><td class="num">${fmtEUR(total)}</td></tr>
        </tfoot>
      </table>
    </body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc || !iframe.contentWindow) {
      document.body.removeChild(iframe);
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();

    const cw = iframe.contentWindow;
    // Wait one tick so the doc has parsed before we print.
    setTimeout(() => {
      try {
        cw.focus();
        cw.print();
      } finally {
        // Remove after the dialog closes — Chrome closes the dialog
        // before we get focus back, Safari/Firefox keep it open so 2s
        // is the safe lower bound.
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 2000);
      }
    }, 150);
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 15, margin: 0 }}>Billing overview</h3>
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            {booking ? 'Signed-up partners' : 'Signed-up and auto-included partners'} — what to invoice in Odoo.
            Use <strong>Generate invoice</strong> on a row to create the draft
            straight in Odoo (VAT set from the supplier's country), or download
            the CSV / print to PDF as a reference.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onDownloadCsv}
            disabled={billingRows.length === 0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
              padding: '8px 12px', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', background: 'var(--bg-elevated)',
              color: 'var(--ink-2)', cursor: billingRows.length === 0 ? 'not-allowed' : 'pointer',
              opacity: billingRows.length === 0 ? 0.5 : 1,
            }}
          >
            <Icon name="link" size={12} /> Download CSV
          </button>
          <button
            type="button"
            onClick={onPrint}
            disabled={billingRows.length === 0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
              padding: '8px 12px', textDecoration: 'none',
              borderRadius: 'var(--r-md)', background: 'var(--ink)', color: 'var(--bg)',
              border: 'none',
              cursor: billingRows.length === 0 ? 'not-allowed' : 'pointer',
              opacity: billingRows.length === 0 ? 0.5 : 1,
            }}
          >
            <Icon name="image" size={12} /> Print / save as PDF
          </button>
        </div>
      </div>

      {billingRows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
          Nothing to invoice yet. Brands show up here once they're marked{' '}
          <strong>Signed up</strong>{booking ? '' : <> or <strong>Auto-included</strong></>} on the Outreach tab
          and their amount is above €0.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'var(--bg-subtle)' }}>
                <th style={{ padding: '8px 10px' }}>Brand</th>
                <th style={{ padding: '8px 10px' }}>Contact</th>
                {booking ? (
                  <th style={{ padding: '8px 10px' }}>Booked</th>
                ) : (
                  <>
                    <th style={{ padding: '8px 10px' }}>Pack</th>
                    <th style={{ padding: '8px 10px' }}>Add-ons</th>
                  </>
                )}
                <th style={{ padding: '8px 10px' }}>Discount</th>
                <th style={{ padding: '8px 10px' }}>Settled by</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {billingRows.map((r) => (
                <tr key={r.brandId} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{r.brandName}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {r.contactName || <span style={{ color: 'var(--ink-4)' }}>—</span>}
                    {r.contactEmail && (
                      <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{r.contactEmail}</div>
                    )}
                  </td>
                  {booking ? (
                    <td style={{ padding: '8px 10px', color: 'var(--ink-2)', fontSize: 12.5, lineHeight: 1.5 }}>
                      {r.bookedLines.length > 0
                        ? r.bookedLines.map((l, i) => <div key={i}>{l}</div>)
                        : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                    </td>
                  ) : (
                    <>
                      <td style={{ padding: '8px 10px' }}>{r.packName}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--ink-2)' }}>
                        {r.addonLabels.length > 0 ? r.addonLabels.join(' + ') : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                      </td>
                    </>
                  )}
                  <td style={{ padding: '8px 10px', color: 'var(--ink-2)' }}>
                    {r.discount || <span style={{ color: 'var(--ink-4)' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <SettlementCell row={r} campaignId={campaignId} />
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                    {fmtEUR(r.amount)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <InvoiceCell row={r} campaignId={campaignId} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--ink)' }}>
                <td colSpan={booking ? 5 : 6} style={{ padding: '10px', fontWeight: 700 }}>Total</td>
                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700 }}>{fmtEUR(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

/* ---------- Results tab — proof-of-delivery + per-asset metrics ---------- */

const ResultsPanel = ({
  campaignId,
  campaignName,
  partnership,
  rows,
}: {
  campaignId: string;
  campaignName: string;
  partnership: Partnership;
  rows: PartnershipResponse[];
}) => {
  const { data: allAssets = [] } = useAssets({ campaign: campaignId });
  const { data: campaign } = useCampaign(campaignId);
  const { data: brands = [] } = useBrands();
  const updateAsset = useUpdateAsset();
  const toast = useToast();

  // v1 channels — newsletter + Instagram only. Homepage / Collection come later.
  const SUPPORTED_TYPES = new Set<CampaignAsset['asset_type']>([
    'Newsletter',
    'Instagram Post', 'Instagram Story', 'Instagram Reel', 'Instagram Giveaway',
  ]);

  const [brandFilter, setBrandFilter] = useState<string>('');
  const [drafts, setDrafts] = useState<Record<number, Record<string, string>>>({});

  // Brand ids that actually paid for a pack (confirmed/auto-included AND
  // selected_pack has a non-zero price). Free-of-charge participants like
  // the Essential pack aren't billed and don't surface in the Results
  // filter — keeps the view focused on the brands we have to report to.
  const booking = !partnership.uses_packs;
  const paidBrandIds = useMemo(() => {
    const packPriceById = new Map(
      partnership.packs.map((p) => [p.id, Number(p.price) || 0]),
    );
    const ids = new Set<string>();
    for (const r of rows) {
      if (r.status !== 'confirmed' && r.status !== 'auto-included') continue;
      // Booking pages: every brand that signed up for at least one slot.
      if (booking) {
        if ((r.booked_slots ?? []).length) ids.add(r.brand.id);
        continue;
      }
      const price = packPriceById.get(r.selected_pack) ?? 0;
      if (price > 0) ids.add(r.brand.id);
    }
    return ids;
  }, [partnership, rows, booking]);

  // Brands shown in the filter row: only paid-pack participants (booking
  // pages: the brands that signed up).
  const campaignBrands = useMemo(() => {
    if (booking) {
      return rows
        .filter((r) => paidBrandIds.has(r.brand.id))
        .map((r) => ({ id: r.brand.id, name: r.brand.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const linked = campaign?.brand_details ?? [];
    let candidates: typeof linked;
    if (linked.length > 0) {
      candidates = linked;
    } else {
      const ids = new Set<string>();
      allAssets.forEach((a) => {
        (a.brands as string[]).forEach((b) => ids.add(b));
        if (a.dedicated_brand) ids.add(a.dedicated_brand);
      });
      candidates = brands.filter((b) => ids.has(b.id));
    }
    return candidates.filter((b) => paidBrandIds.has(b.id)).map((b) => ({ id: b.id, name: b.name }));
  }, [allAssets, brands, campaign, paidBrandIds, booking, rows]);

  const matchesBrand = (a: CampaignAsset): boolean => {
    if (!brandFilter) return true;
    return (a.brands as string[]).includes(brandFilter) || a.dedicated_brand === brandFilter;
  };

  const filteredAssets = useMemo(
    () => allAssets
      // Booking pages: also every placement a signed-up brand is on (homepage,
      // collection…) — proof only, no numbers for those yet.
      .filter((a) => SUPPORTED_TYPES.has(a.asset_type)
        || (booking && (a.brands as string[]).some((b) => paidBrandIds.has(b))))
      .filter(matchesBrand)
      .sort((a, b) => (a.publish_date ?? '').localeCompare(b.publish_date ?? '')),
    [allAssets, brandFilter, booking, paidBrandIds],
  );

  const draftFor = (a: CampaignAsset, key: string): string => {
    const d = drafts[a.id];
    if (d && key in d) return d[key];
    const v = a.metrics?.[key];
    return v == null ? '' : String(v);
  };

  const setDraft = (assetId: number, key: string, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [assetId]: { ...(prev[assetId] ?? {}), [key]: value },
    }));
  };

  const saveMetrics = (a: CampaignAsset) => {
    const d = drafts[a.id];
    if (!d) return;
    const fields = METRIC_FIELDS_BY_CATEGORY[a.category] ?? [];
    const next: Record<string, number | string | null> = { ...(a.metrics ?? {}) };
    fields.forEach((f) => {
      if (!(f.key in d)) return;
      const raw = d[f.key].trim();
      if (raw === '') { next[f.key] = null; return; }
      const n = parseFloat(raw.replace(/[,\s]/g, ''));
      next[f.key] = Number.isFinite(n) ? n : null;
    });
    updateAsset.mutate(
      { id: a.id, patch: { metrics: next } as Partial<CampaignAsset> },
      {
        onSuccess: () => {
          toast.push(`Saved metrics for "${a.title || a.asset_type}"`);
          setDrafts((prev) => { const { [a.id]: _, ...rest } = prev; return rest; });
        },
        onError: () => toast.push('Could not save metrics'),
      },
    );
  };

  const hasDirty = (assetId: number): boolean => !!drafts[assetId];

  const pdfHref = brandFilter
    ? `/campaigns/${campaignId}/lookbook?brand=${encodeURIComponent(brandFilter)}`
    : `/campaigns/${campaignId}/lookbook`;
  const pdfLabel = brandFilter
    ? `Open partner PDF — ${campaignBrands.find((b) => b.id === brandFilter)?.name ?? ''}`
    : 'Open campaign PDF';

  // Brand-specific shareable results page URL — uses the existing
  // per-brand opt_in_token so we don't issue a separate share token.
  const selectedRow = brandFilter
    ? rows.find((r) => r.brand.id === brandFilter && (r.status === 'confirmed' || r.status === 'auto-included'))
    : null;
  const shareUrl = selectedRow
    ? `${window.location.origin}/results/${partnership.slug}?token=${encodeURIComponent(selectedRow.opt_in_token)}`
    : '';
  const [shareCopied, setShareCopied] = useState(false);
  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.push(`Copied ${selectedRow?.brand.name}'s results link`);
      setTimeout(() => setShareCopied(false), 1500);
    } catch {
      toast.push('Could not copy — your browser blocked it');
    }
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 15, margin: 0 }}>Results</h3>
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            {booking ? (
              <>
                Proof of delivery for every placement a brand booked — a live link or a screenshot. Newsletter
                and Instagram also take numbers (from Klaviyo / Meta Business Suite); rates compute automatically.
                Pick a brand to see only its placements and copy its results link.
              </>
            ) : (
              <>
                Proof of delivery and post-publish metrics — Newsletter & Instagram for now.
                Numbers from Klaviyo / Meta Business Suite; rates compute automatically.
                Filter only lists brands on a paid pack (Boost / Premium); free
                participants are skipped.
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {selectedRow && (
            <button
              type="button"
              onClick={copyShareLink}
              title={`Copy a shareable results page link for ${selectedRow.brand.name}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                padding: '8px 12px',
                borderRadius: 'var(--r-md)',
                background: shareCopied ? '#e7f6ec' : 'var(--bg-elevated)',
                color: shareCopied ? '#0d7a36' : 'var(--ink-2)',
                border: '1px solid var(--border)', cursor: 'pointer',
              }}
            >
              <Icon name={shareCopied ? 'check' : 'link'} size={12} />
              {shareCopied ? 'Copied' : `Copy share link — ${selectedRow.brand.name}`}
            </button>
          )}
          <a
            href={pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
              padding: '8px 12px', textDecoration: 'none',
              borderRadius: 'var(--r-md)', background: 'var(--ink)', color: 'var(--bg)',
            }}
          >
            <Icon name="image" size={12} /> {pdfLabel}
          </a>
        </div>
      </div>

      {/* Brand filter row */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          type="button"
          className={`filter-pill ${brandFilter === '' ? 'active' : ''}`}
          onClick={() => setBrandFilter('')}
        >
          All brands
        </button>
        {campaignBrands.map((b) => (
          <button
            key={b.id}
            type="button"
            className={`filter-pill ${brandFilter === b.id ? 'active' : ''}`}
            onClick={() => setBrandFilter(b.id)}
          >
            {b.name}
          </button>
        ))}
      </div>

      {filteredAssets.length === 0 ? (
        <div style={{ padding: 20, fontSize: 13, color: 'var(--ink-4)', textAlign: 'center' }}>
          {booking ? 'No booked placements yet' : 'No newsletter or Instagram assets yet'}
          {brandFilter ? ' for the selected brand' : ''}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filteredAssets.map((a) => {
            const fields = METRIC_FIELDS_BY_CATEGORY[a.category] ?? [];
            const liveMetrics: Record<string, number | string | null> = { ...(a.metrics ?? {}) };
            fields.forEach((f) => {
              const d = drafts[a.id]?.[f.key];
              if (d != null) {
                const t = d.trim();
                liveMetrics[f.key] = t === '' ? null : parseFloat(t.replace(/[,\s]/g, ''));
              }
            });
            const rates = computeRates(a.category, liveMetrics);
            return (
              <div
                key={a.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '180px 1fr',
                  gap: 16,
                  padding: 12,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  background: 'var(--bg-elevated)',
                }}
              >
                {/* Proof preview */}
                <div
                  style={{
                    width: 180,
                    height: 180,
                    background: 'var(--bg-subtle)',
                    borderRadius: 'var(--r-sm)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {a.proof_screenshot ? (
                    <img
                      src={a.proof_screenshot}
                      alt={a.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : a.proof_url ? (
                    <a
                      href={a.proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 11, color: 'var(--ink-3)', padding: 8, textAlign: 'center' }}
                    >
                      {a.proof_url}
                    </a>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>No proof yet</span>
                  )}
                </div>

                {/* Form */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{a.title || a.asset_type}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                        {a.asset_type}
                        {a.publish_date ? ` · ${fmtShort(a.publish_date)}` : ' · Not yet scheduled'}
                      </div>
                    </div>
                    {hasAnyMetric(a.category, a.metrics) && !hasDirty(a.id) && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 999, background: '#e7f6ec', color: '#0d7a36', fontWeight: 600 }}>
                        Saved
                      </span>
                    )}
                  </div>

                  <ProofEditor asset={a} />

                  {fields.length === 0 ? (
                    <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                      No numbers for this placement type yet — the proof shows the brand it went live.
                    </div>
                  ) : (<>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${fields.length}, 1fr)`, gap: 8 }}>
                    {fields.map((f) => (
                      <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 10.5, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {f.label}
                        </span>
                        <input
                          className="input"
                          type="text"
                          inputMode="numeric"
                          placeholder="—"
                          value={draftFor(a, f.key)}
                          onChange={(e) => setDraft(a.id, f.key, e.target.value)}
                          style={{ fontSize: 13 }}
                        />
                      </label>
                    ))}
                  </div>

                  {rates.length > 0 && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
                      {rates.map((r) => (
                        <span key={r.label} style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {r.label}: <strong style={{ color: 'var(--ink)' }}>{r.value}</strong>
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ marginTop: 4 }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => saveMetrics(a)}
                      disabled={!hasDirty(a.id) || updateAsset.isPending}
                      style={{
                        fontSize: 12,
                        padding: '5px 12px',
                        borderRadius: 'var(--r-sm)',
                        border: '1px solid var(--border)',
                        background: hasDirty(a.id) ? 'var(--ink)' : 'var(--bg-subtle)',
                        color: hasDirty(a.id) ? 'var(--bg)' : 'var(--ink-4)',
                        cursor: hasDirty(a.id) ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Save
                    </button>
                  </div>
                  </>)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 16, lineHeight: 1.5 }}>
        Campaign: <strong>{campaignName}</strong>. Homepage / Collection / Revenue fields will be added as a v2.
      </div>
    </div>
  );
};

/** Proof of delivery on a Results card: the live link, and a screenshot
 *  (upload or paste). Saved on the placement straight away. */
const ProofEditor = ({ asset }: { asset: CampaignAsset }) => {
  const toast = useToast();
  const updateAsset = useUpdateAsset();
  const save = (patch: Partial<CampaignAsset>, done: string) =>
    updateAsset.mutate(
      { id: asset.id, patch },
      {
        onSuccess: () => toast.push(done),
        onError: () => toast.push('Could not save the proof — check the link starts with https://'),
      },
    );
  const addShot = async (file: File | undefined | null) => {
    if (!file) return;
    try {
      const pic = await readPicture(file);
      save({ proof_screenshot: pic.image }, 'Screenshot saved');
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Could not read the picture.');
    }
  };
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
      <span style={{ fontSize: 10.5, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Proof</span>
      <span style={{ flex: '1 1 200px' }}>
        <CommitInput
          value={asset.proof_url ?? ''}
          placeholder="Live link (https://…)"
          onCommit={(v) => { if (v.trim() !== (asset.proof_url ?? '')) save({ proof_url: v.trim() }, 'Link saved'); }}
        />
      </span>
      <label className="link" style={{ cursor: 'pointer' }}>
        {asset.proof_screenshot ? 'Replace screenshot…' : 'Add screenshot…'}
        <input type="file" accept="image/*" hidden onChange={(e) => { const input = e.currentTarget; addShot(input.files?.[0]).finally(() => { input.value = ''; }); }} />
      </label>
      <button
        type="button"
        className="link"
        onClick={async () => { try { await addShot(await pastedPicture()); } catch (err) { toast.push(err instanceof Error ? err.message : 'Could not paste.'); } }}
      >
        paste
      </button>
      {asset.proof_screenshot && (
        <button type="button" className="link" onClick={() => save({ proof_screenshot: '' }, 'Screenshot removed')}>remove</button>
      )}
    </div>
  );
};

/* ---------- Generate draft placements button + result modal ---------- */

const BILLABLE_FOR_GENERATE = new Set<PartnershipResponseStatus>([
  'confirmed', 'auto-included',
]);

const GenerateDraftPlacementsButton = ({
  campaignId,
  rows,
}: {
  campaignId: string;
  rows: PartnershipResponse[];
}) => {
  const generate = useGenerateDraftPlacements(campaignId);
  const toast = useToast();
  // 'preview' = dry-run result is showing, user can pick which top-ups
  // to apply. 'done' = real result is showing.
  const [phase, setPhase] = useState<'preview' | 'done' | null>(null);
  const [summary, setSummary] = useState<GeneratedPlacementsSummary | null>(null);
  const [skipUpdateIds, setSkipUpdateIds] = useState<Set<number>>(new Set());
  const billableCount = rows.filter((r) => BILLABLE_FOR_GENERATE.has(r.status)).length;
  const disabled = billableCount === 0 || generate.isPending;

  const startPreview = async () => {
    try {
      const data = await generate.mutateAsync({ dryRun: true });
      setSkipUpdateIds(new Set());
      setSummary(data);
      setPhase('preview');
    } catch {
      toast.push('Could not preview placements');
    }
  };

  const applyPlan = async () => {
    try {
      const data = await generate.mutateAsync({
        skipUpdateIds: Array.from(skipUpdateIds),
      });
      setSummary(data);
      setPhase('done');
      const totalNew = data.created.length;
      const totalTopUp = data.updated.length;
      if (totalNew + totalTopUp === 0) {
        toast.push('Nothing was applied.');
      } else {
        toast.push(
          `Created ${totalNew} new placement${totalNew === 1 ? '' : 's'}`
          + (totalTopUp > 0 ? ` · added brands to ${totalTopUp} existing` : ''),
        );
      }
    } catch {
      toast.push('Could not apply placements');
    }
  };

  const close = () => { setPhase(null); setSummary(null); setSkipUpdateIds(new Set()); };

  const toggleSkip = (id: number) => {
    setSkipUpdateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={startPreview}
        disabled={disabled}
        title={billableCount === 0
          ? 'Confirm at least one brand first.'
          : 'Preview the placements we would create / update based on confirmed brands + their picks'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
          padding: '8px 12px', textDecoration: 'none',
          borderRadius: 'var(--r-md)',
          background: disabled ? 'var(--bg-subtle)' : 'var(--ink)',
          color: disabled ? 'var(--ink-4)' : 'var(--bg)',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          flexShrink: 0,
        }}
      >
        <Icon name="plus" size={12} />
        {generate.isPending && !summary ? 'Previewing…' : 'Generate draft placements'}
      </button>

      {summary && phase && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{
              width: 560, maxHeight: '85vh', overflowY: 'auto',
              padding: 20, background: 'var(--bg-elevated)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 15, margin: 0 }}>
                  {phase === 'preview' ? 'Preview — review before applying' : 'Draft placements applied'}
                </h3>
                <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
                  {phase === 'preview'
                    ? 'Nothing has been saved yet. Untick any top-up you want to handle manually, then Apply.'
                    : 'Dates default to the campaign start — finetune on the Placements section.'}
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="icon-btn"
                title="Close"
                style={{ width: 28, height: 28 }}
              >
                <Icon name="x" size={14} />
              </button>
            </div>

            {summary.created.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 700, marginBottom: 8 }}>
                  {phase === 'preview' ? 'Will create' : 'Created'} · {summary.created.length}
                </div>
                {summary.created.map((c, idx) => (
                  <div key={idx} style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', marginBottom: 6, background: 'var(--bg)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.title}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                      {c.asset_type} · {c.brands.length} brand{c.brands.length === 1 ? '' : 's'}: {c.brands.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {summary.updated.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 700, marginBottom: 8 }}>
                  {phase === 'preview'
                    ? `Add brands to existing · ${summary.updated.length} (pick which)`
                    : `Added brands to existing · ${summary.updated.length}`}
                </div>
                {summary.updated.map((u) => {
                  const skip = skipUpdateIds.has(u.asset_id);
                  return (
                    <div
                      key={u.asset_id}
                      style={{
                        padding: '8px 10px', border: '1px solid var(--border)',
                        borderRadius: 'var(--r-sm)', marginBottom: 6,
                        background: phase === 'preview' && skip ? 'var(--bg-subtle)' : 'var(--bg)',
                        opacity: phase === 'preview' && skip ? 0.55 : 1,
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                      }}
                    >
                      {phase === 'preview' && (
                        <label style={{ paddingTop: 2, cursor: 'pointer' }} title={skip ? 'Click to include' : 'Click to skip — you\'ll add the brand manually'}>
                          <input
                            type="checkbox"
                            checked={!skip}
                            onChange={() => toggleSkip(u.asset_id)}
                          />
                        </label>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{u.title}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                          → {phase === 'preview' && skip ? 'Will NOT add' : 'Add'}: {u.added_brands.join(', ')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {summary.skipped.length > 0 && phase === 'done' && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 700, marginBottom: 8 }}>
                  No change · {summary.skipped.length}
                </div>
                {summary.skipped.map((s, idx) => (
                  <div key={idx} style={{ fontSize: 12, color: 'var(--ink-3)', padding: '4px 0' }}>
                    <strong>{s.item}:</strong> {s.reason}
                  </div>
                ))}
              </div>
            )}

            {summary.created.length === 0 && summary.updated.length === 0 && summary.skipped.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--ink-4)', padding: 16, textAlign: 'center' }}>
                Nothing to generate — none of the confirmed brands have packs/add-ons mapped to placements yet. Tag them on the partnership template editor first.
              </div>
            )}

            {/* Footer actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              {phase === 'preview' ? (
                <>
                  <button
                    type="button"
                    onClick={close}
                    style={{
                      padding: '8px 14px', borderRadius: 'var(--r-md)',
                      border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                      color: 'var(--ink-2)', cursor: 'pointer', fontSize: 13,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyPlan}
                    disabled={generate.isPending || (summary.created.length === 0 && summary.updated.length - skipUpdateIds.size === 0)}
                    style={{
                      padding: '8px 14px', borderRadius: 'var(--r-md)',
                      border: 'none', background: 'var(--ink)', color: 'var(--bg)',
                      cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {generate.isPending ? 'Applying…' : 'Apply'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={close}
                  style={{
                    padding: '8px 14px', borderRadius: 'var(--r-md)',
                    border: 'none', background: 'var(--ink)', color: 'var(--bg)',
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const PartnersTable = ({
  partnership,
  campaignId,
}: {
  partnership: Partnership;
  campaignId: string;
}) => {
  const statusOptions = partnership.uses_packs ? STATUS_OPTIONS : BOOKING_STATUS_OPTIONS;
  const responsesQ = usePartnershipResponses(campaignId);
  const updateMut = useUpdatePartnershipResponse(campaignId);
  const deleteMut = useDeletePartnershipResponse(campaignId);
  const addBrandMut = useAddBrandToTrack(campaignId);
  const addAllMut = useAddCampaignBrands(campaignId);
  // Booking pages: free slots for "+ Add slot" on a row.
  const placementsQ = usePartnershipPlacements(!partnership.uses_packs ? campaignId : undefined);
  const { data: outreachCampaign } = useCampaign(campaignId);
  const { data: allBrands = [] } = useBrands();
  const { data: team = [] } = useTeam();
  const { data: contactShortlist = [] } = useContactShortlist();
  const toast = useToast();

  // Only the partnership contacts (Doreen / Robbie / Yann) can be the
  // assignee — anyone else on the mosy team isn't part of the outreach.
  // If duplicate Django accounts share an email, keep the highest user_id
  // (the canonical / most recently provisioned one).
  const assignableEmails = useMemo(
    () => new Set(contactShortlist.map((c) => c.email.toLowerCase())),
    [contactShortlist],
  );
  const assignableUsers = useMemo(() => {
    const byEmail = new Map<string, typeof team[number]>();
    for (const u of team) {
      const email = (u.email || '').toLowerCase();
      if (!assignableEmails.has(email)) continue;
      const existing = byEmail.get(email);
      if (!existing || u.user_id > existing.user_id) byEmail.set(email, u);
    }
    return Array.from(byEmail.values());
  }, [team, assignableEmails]);

  const rows = responsesQ.data ?? [];
  const counts = useMemo(() => {
    const c: Record<PartnershipResponseStatus, number> = {
      pending: 0, contacted: 0, 'sent-reminder': 0, 'follow-up': 0, replied: 0,
      confirmed: 0, 'auto-included': 0, declined: 0, excluded: 0,
    };
    rows.forEach((r) => { c[r.status] = (c[r.status] ?? 0) + 1; });
    return c;
  }, [rows]);

  // Pack-level "X / capacity" counts so Doreen sees at a glance how
  // many of each tier have been confirmed/auto-included and how many
  // seats are left. Capacity = optional cap configured per pack;
  // when not set, the cap reads as "—".
  const packCounts = useMemo(() => {
    return partnership.packs.map((p) => {
      const confirmed = rows.filter(
        (r) => r.selected_pack === p.id
          && (r.status === 'confirmed' || r.status === 'auto-included'),
      ).length;
      return { id: p.id, name: p.name, confirmed, capacity: p.capacity };
    });
  }, [partnership.packs, rows]);

  // Per-column filters — Notion-style multi-select dropdowns in each
  // column header. All filters combine (AND).
  const [brandSearch, setBrandSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set());
  const [packFilter, setPackFilter] = useState<Set<string>>(new Set());

  const filteredRows = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.brand.name.toLowerCase().includes(q)) return false;
      if (statusFilter.size > 0 && !statusFilter.has(r.status)) return false;
      if (assigneeFilter.size > 0) {
        const key = r.assigned_to == null ? '__none__' : String(r.assigned_to);
        if (!assigneeFilter.has(key)) return false;
      }
      if (packFilter.size > 0) {
        const key = r.selected_pack || '__none__';
        if (!packFilter.has(key)) return false;
      }
      return true;
    });
  }, [rows, brandSearch, statusFilter, assigneeFilter, packFilter]);

  const filterActive =
    brandSearch.trim().length > 0
    || statusFilter.size > 0
    || assigneeFilter.size > 0
    || packFilter.size > 0;

  const clearAllFilters = () => {
    setBrandSearch('');
    setStatusFilter(new Set());
    setAssigneeFilter(new Set());
    setPackFilter(new Set());
  };

  const linkedIds = new Set(rows.map((r) => r.brand.id));
  const addable = allBrands.filter((b) => !linkedIds.has(b.id));
  // Booking pages: Outreach is its own list — campaign brands not on it yet.
  const booking = !partnership.uses_packs;
  // Booking pages: no add-ons or packs; Discount only when the page asks
  // brands for a minimum shopper discount (Doreen, 2026-10-27).
  const showDiscount = !booking || partnership.min_discount_pct != null;
  const signedUp = rows.filter((r) => r.status === 'confirmed');
  const bookedSlots = signedUp.reduce((n, r) => n + (r.booked ?? []).length, 0);
  const bookedTotal = signedUp.reduce((sum, r) => sum + (Number(r.effective_price) || 0), 0);
  const campaignOnly = (outreachCampaign?.brand_details ?? []).filter((b) => !linkedIds.has(b.id));

  const apply = (id: number, patch: Partial<PartnershipResponse>) => {
    updateMut.mutate({ id, patch });
  };

  const onAddBrand = (brandId: string) => {
    if (!brandId) return;
    addBrandMut.mutate(brandId, {
      onError: () => toast.push('Could not add brand'),
    });
  };

  const onDelete = (row: PartnershipResponse) => {
    if (!window.confirm(booking ? `Remove ${row.brand.name} from Outreach? It stays in the campaign and on its placements.` : `Remove ${row.brand.name} from the tracking list?`)) return;
    deleteMut.mutate(row.id);
  };

  return (
    <div>
      {/* Pack-by-pack confirmed counts vs capacity. Shown above the
          status counters so it's the first thing Doreen sees on the
          Outreach tab. Skip packs with zero capacity AND zero
          confirmations to avoid clutter. */}
      {!booking && packCounts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 600 }}>
            Packs
          </span>
          {packCounts.map((p) => {
            const atCap = p.capacity != null && p.confirmed >= p.capacity;
            return (
              <span
                key={p.id}
                title={p.capacity != null
                  ? `${p.confirmed} confirmed of ${p.capacity} max for ${p.name}`
                  : `${p.confirmed} confirmed on ${p.name} — no cap set`}
                style={{
                  display: 'inline-flex', alignItems: 'baseline', gap: 5,
                  padding: '3px 10px', borderRadius: 'var(--r-pill)',
                  background: atCap ? '#fde7e7' : 'var(--bg-subtle)',
                  color: atCap ? '#8b1d1d' : 'var(--ink-2)',
                  fontSize: 12, fontWeight: 600,
                }}
              >
                <span style={{ fontSize: 13 }}>
                  {p.confirmed}
                  <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>
                    /{p.capacity ?? '—'}
                  </span>
                </span>
                <span style={{ fontWeight: 500, color: atCap ? '#8b1d1d' : 'var(--ink-3)', fontSize: 11.5 }}>
                  {p.name}
                </span>
              </span>
            );
          })}
        </div>
      )}

      {/* counters + clear-all */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, fontSize: 12, alignItems: 'center' }}>
        <span style={{ color: 'var(--ink-4)' }}>
          {filterActive
            ? <>Showing {filteredRows.length} of {rows.length} brands</>
            : <>{rows.length} brand{rows.length === 1 ? '' : 's'} {booking ? 'on the list' : 'tracked'}</>}
        </span>
        {booking && signedUp.length > 0 && (
          <span style={{ padding: '2px 8px', borderRadius: 'var(--r-pill)', background: '#e6f4ea', color: '#1e7a3c', fontSize: 12, fontWeight: 600 }}>
            {bookedSlots} slot{bookedSlots === 1 ? '' : 's'} · {fmtMoney(bookedTotal)} booked
          </span>
        )}
        {statusOptions.map((s) => counts[s.value] > 0 && (
          <span key={s.value} style={{
            padding: '2px 8px', borderRadius: 'var(--r-pill)',
            background: STATUS_COLORS[s.value].bg, color: STATUS_COLORS[s.value].fg,
            fontSize: 12,
          }}>
            {counts[s.value]} {s.label.toLowerCase()}
          </span>
        ))}
        {filterActive && (
          <button
            type="button"
            onClick={clearAllFilters}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', padding: '3px 10px', cursor: 'pointer',
              fontSize: 12, color: 'var(--ink-4)', marginLeft: 'auto',
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* add brand */}
      {addable.length > 0 && (
        <div className="m-wrap" style={{ marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="input"
            value=""
            onChange={(e) => { onAddBrand(e.target.value); e.target.value = ''; }}
            disabled={addBrandMut.isPending}
            style={{ maxWidth: 320 }}
          >
            <option value="">{booking ? '+ Add brand to reach out to…' : '+ Add brand to track…'}</option>
            {addable.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          {booking && campaignOnly.length > 0 && (
            <Button
              variant="ghost"
              disabled={addAllMut.isPending}
              title={campaignOnly.map((b) => b.name).join(', ')}
              onClick={() => addAllMut.mutate(undefined, {
                onSuccess: (r) => toast.push(`${r.added} campaign brand${r.added === 1 ? '' : 's'} added to Outreach`),
                onError: () => toast.push('Could not add the campaign brands'),
              })}
            >
              Add all campaign brands ({campaignOnly.length})
            </Button>
          )}
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            {booking
              ? 'Only the brands you want to sell to. Featuring a brand on a placement doesn\'t put it here; a brand joins the campaign when it books.'
              : partnership.track_deals
                ? 'Brands join the campaign only once their row is set to Signed up.'
                : 'Adding a brand also links it to the campaign.'}
          </span>
        </div>
      )}

      {/* table */}
      {rows.length === 0 ? (
        <div style={{ padding: 16, fontSize: 13, color: 'var(--ink-4)' }}>
          No brands linked yet. Add one above (or link brands to the campaign).
        </div>
      ) : filteredRows.length === 0 ? (
        <div style={{ padding: 16, fontSize: 13, color: 'var(--ink-4)' }}>
          No brands match the current filter.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <th style={{ padding: '8px 6px', minWidth: 120 }}>
                  Brand
                  <ColumnFilter
                    search={brandSearch}
                    onSearchChange={setBrandSearch}
                  />
                </th>
                <th style={{ padding: '8px 6px', minWidth: 120 }}>
                  Assigned to
                  <ColumnFilter
                    selected={assigneeFilter}
                    onChange={setAssigneeFilter}
                    options={[
                      { value: '__none__', label: 'Unassigned' },
                      ...assignableUsers.map((u) => ({
                        value: String(u.user_id),
                        label: u.display_name || u.email,
                      })),
                    ]}
                  />
                </th>
                <th style={{ padding: '8px 6px', minWidth: 110 }}>
                  Status
                  <ColumnFilter
                    selected={statusFilter}
                    onChange={setStatusFilter}
                    options={statusOptions.map((s) => ({ value: s.value, label: s.label }))}
                  />
                </th>
                {!partnership.uses_packs ? (
                  <>
                    <th style={{ padding: '8px 6px', minWidth: 220 }} title="What the brand booked on the booking page, at the price on sign-up">Booked</th>
                    <th style={{ padding: '8px 6px', minWidth: 110 }} title="When the brand signed up — first to sign up gets the slot">Signed up on</th>
                  </>
                ) : (
                <th style={{ padding: '8px 6px', minWidth: 120 }}>
                  Pack
                  <ColumnFilter
                    selected={packFilter}
                    onChange={setPackFilter}
                    options={[
                      { value: '__none__', label: '— (no pack)' },
                      ...partnership.packs.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                </th>
                )}
                {showDiscount && <th style={{ padding: '8px 6px', minWidth: 80 }}>Discount</th>}
                {partnership.track_deals && (
                  <th style={{ padding: '8px 6px', minWidth: 60 }} title="Stock order placed for this brand">Order</th>
                )}
                {partnership.track_deals && (
                  <th style={{ padding: '8px 6px', minWidth: 230 }} title="When the brand's own promo runs (start → end)">Promo timing</th>
                )}
                {!booking && <th style={{ padding: '8px 6px', minWidth: 140 }}>Add-ons</th>}
                <th style={{ padding: '8px 6px', minWidth: 100 }} title={booking ? 'From the sign-up — type an amount to correct it' : undefined}>{booking ? 'Total' : 'Final price'}</th>
                <th style={{ padding: '8px 6px', minWidth: 220 }}>Note</th>
                <th style={{ padding: '8px 6px', width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    borderTop: '1px solid var(--border)',
                    opacity: row.status === 'excluded' ? 0.45 : 1,
                  }}
                >
                  <td style={{ padding: '8px 6px', fontWeight: 600 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <DiscountStanceDot stance={row.brand.discount_stance} />
                      <Link
                        to={`/brands/${row.brand.id}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                        onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                        title="Open brand page"
                      >
                        {row.brand.name}
                      </Link>
                      <ComposeEmailButton
                        brand={row.brand}
                        token={row.opt_in_token}
                        partnership={partnership}
                      />
                      <ConfirmEmailButton
                        row={row}
                        partnership={partnership}
                      />
                      <CopyOptInLinkButton
                        slug={partnership.slug}
                        token={row.opt_in_token}
                        brandName={row.brand.name}
                      />
                    </div>
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    <select
                      className="input"
                      value={row.assigned_to ?? ''}
                      onChange={(e) => apply(row.id, {
                        assigned_to: e.target.value ? Number(e.target.value) : null,
                      })}
                    >
                      <option value="">—</option>
                      {assignableUsers.map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {u.display_name || u.email}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    <select
                      className="input"
                      value={row.status}
                      onChange={(e) => {
                        const next = e.target.value as PartnershipResponseStatus;
                        // Stamp the timestamps automatically so they're
                        // still captured even though the checkboxes are
                        // gone — useful for the future activity log.
                        const patch: Partial<PartnershipResponse> = { status: next };
                        if (next === 'contacted' && !row.contacted_at) {
                          patch.contacted_at = new Date().toISOString();
                        }
                        if ((next === 'replied' || next === 'confirmed' || next === 'declined')) {
                          if (!row.contacted_at) patch.contacted_at = new Date().toISOString();
                          if (!row.replied_at) patch.replied_at = new Date().toISOString();
                        }
                        apply(row.id, patch);
                      }}
                      style={{
                        background: STATUS_COLORS[row.status].bg,
                        color: STATUS_COLORS[row.status].fg,
                        fontWeight: 600,
                      }}
                    >
                      {statusOptions.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  {!partnership.uses_packs ? (
                  <>
                  <td style={{ padding: '8px 6px', fontSize: 12 }}>
                    <BookedCell row={row} campaignId={campaignId} placements={placementsQ.data} />
                  </td>
                  <td style={{ padding: '8px 6px', fontSize: 12, whiteSpace: 'nowrap', color: row.terms_accepted_at ? 'var(--ink-2)' : 'var(--ink-5)' }}>
                    {row.terms_accepted_at ? fmtSignedUp(row.terms_accepted_at) : '—'}
                  </td>
                  </>
                  ) : (
                  <td style={{ padding: '8px 6px' }}>
                    <select
                      className="input"
                      value={row.selected_pack}
                      onChange={(e) => apply(row.id, { selected_pack: e.target.value })}
                    >
                      <option value="">—</option>
                      {partnership.packs.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.sold_out ? ' — no longer available' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  )}
                  {showDiscount && (
                  <td style={{ padding: '8px 6px' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="input"
                        defaultValue={row.discount}
                        placeholder="e.g. 15%"
                        title={row.discount_auto
                          ? `Auto-filled from ${row.brand.name}'s permanent discount. Type a value to override.`
                          : undefined}
                        style={{
                          fontStyle: row.discount_auto ? 'italic' : 'normal',
                          paddingRight: row.discount_auto ? 44 : undefined,
                        }}
                        onBlur={(e) => {
                          if (e.target.value !== row.discount) apply(row.id, { discount: e.target.value });
                        }}
                      />
                      {row.discount_auto && (
                        <span
                          title={`Pulled from ${row.brand.name}'s permanent discount on the brand page. Type a value to override.`}
                          style={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: 9.5,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            padding: '2px 5px',
                            borderRadius: 4,
                            background: 'var(--bg-subtle)',
                            color: 'var(--ink-4)',
                            pointerEvents: 'none',
                          }}
                        >
                          Auto
                        </span>
                      )}
                    </div>
                  </td>
                  )}
                  {partnership.track_deals && (
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={row.order_done}
                        onChange={(ev) => apply(row.id, { order_done: ev.target.checked })}
                        title="Stock order placed"
                      />
                    </td>
                  )}
                  {partnership.track_deals && (
                    <td style={{ padding: '8px 6px' }}>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <input
                          className="input"
                          type="date"
                          value={row.promo_from ?? ''}
                          onChange={(ev) => apply(row.id, { promo_from: ev.target.value || null })}
                          title="Promo starts"
                        />
                        <span style={{ color: 'var(--ink-4)', fontSize: 11 }}>→</span>
                        <input
                          className="input"
                          type="date"
                          value={row.promo_until ?? ''}
                          onChange={(ev) => apply(row.id, { promo_until: ev.target.value || null })}
                          title="Promo ends"
                        />
                      </div>
                    </td>
                  )}
                  {!booking && (
                  <td style={{ padding: '8px 6px' }}>
                    <AddonMultiSelect
                      packs={partnership.addons}
                      selected={row.selected_addons}
                      onChange={(ids) => apply(row.id, { selected_addons: ids })}
                    />
                  </td>
                  )}
                  <td style={{ padding: '8px 6px' }}>
                    <FinalPriceCell row={row} apply={apply} />
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    {row.brand_note && row.brand_note.trim() && (
                      <div
                        style={{
                          background: '#f3f4f6',
                          border: '1px solid #e1e3e8',
                          borderRadius: 6,
                          padding: '6px 8px',
                          marginBottom: 6,
                          fontSize: 12,
                          lineHeight: 1.4,
                          color: 'var(--ink-2)',
                          fontStyle: 'italic',
                          whiteSpace: 'pre-wrap',
                        }}
                        title="Left by the brand on the opt-in form"
                      >
                        <div
                          style={{
                            fontSize: 9.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: 'var(--ink-4)',
                            fontWeight: 700,
                            fontStyle: 'normal',
                            marginBottom: 2,
                          }}
                        >
                          From the brand
                        </div>
                        {row.brand_note}
                      </div>
                    )}
                    <textarea
                      className="input"
                      defaultValue={row.note}
                      placeholder="Notes…"
                      rows={2}
                      onBlur={(e) => {
                        if (e.target.value !== row.note) apply(row.id, { note: e.target.value });
                      }}
                      style={{ width: '100%', resize: 'vertical', minHeight: 48, fontFamily: 'inherit', lineHeight: 1.4 }}
                    />
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => onDelete(row)}
                      title={booking ? 'Remove from Outreach' : 'Remove from tracking'}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: 'var(--ink-4)', fontSize: 16, padding: 4,
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PartnersRevenueTotal rows={rows} booking={booking} />
        </div>
      )}
    </div>
  );
};

const AddonMultiSelect = ({
  packs,
  selected,
  onChange,
}: {
  packs: PartnershipAddon[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const labels = packs
    .filter((p) => selected.includes(p.id))
    .map((p) => p.label);
  const summary = labels.length === 0 ? '—' : labels.join(', ');
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="input"
        onClick={() => setOpen((v) => !v)}
        style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
      >
        {summary}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 10,
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)', padding: 8, minWidth: 220,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        }}>
          {packs.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)', padding: 4 }}>
              No add-ons on this partnership.
            </div>
          )}
          {packs.map((a) => {
            const on = selected.includes(a.id);
            return (
              <label key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, a.id]
                      : selected.filter((id) => id !== a.id);
                    onChange(next);
                  }}
                />
                <span style={{ fontSize: 13 }}>{a.label}</span>
              </label>
            );
          })}
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              marginTop: 6, width: '100%', background: 'transparent',
              border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
              padding: '4px 8px', cursor: 'pointer', fontSize: 12,
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};


/* ---------- main editor ---------- */

const CampaignPartnershipEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: campaign, isLoading: campLoading, isError: campError } = useCampaign(id);
  const partnershipQuery = usePartnership(id);
  const { data: contacts = [] } = useContactShortlist();
  const { data: catalog } = usePartnershipCatalog();
  // Shares the cache with PartnersTable's own call (same query key).
  const { data: followUpRows = [] } = usePartnershipResponses(id);

  const createMutation = useCreatePartnership();
  const updateMutation = useUpdatePartnership(id ?? '');
  const publishMutation = usePublishPartnership(id ?? '');
  const deleteMutation = useDeletePartnership();

  const [draft, setDraft] = useState<Partnership | null>(null);
  const [dirty, setDirty] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // ?tab=outreach (e.g. from a "signed up" notification) opens that tab.
  const [activeTab, setActiveTab] = useState<'setup' | 'placements' | 'follow-up' | 'results' | 'billing'>(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'outreach' ? 'follow-up'
      : tab === 'placements' || tab === 'results' || tab === 'billing' ? tab
      : 'setup';
  });

  // Sync the draft from the server when it loads or changes externally.
  useEffect(() => {
    if (partnershipQuery.data) {
      setDraft(partnershipQuery.data);
      setDirty(false);
    }
  }, [partnershipQuery.data]);

  const publicUrl = useMemo(() => {
    if (!draft) return '';
    return `${window.location.origin}/partnerships/${draft.slug}`;
  }, [draft]);

  if (campLoading) {
    return <div className="card" style={{ padding: 32 }}>Loading…</div>;
  }
  if (campError || !campaign || !id) {
    return (
      <div className="card" style={{ padding: 32 }}>
        Campaign not found. <Button variant="ghost" onClick={() => navigate('/campaigns')}>Back</Button>
      </div>
    );
  }

  const partnershipStatus =
    partnershipQuery.error && (partnershipQuery.error as { response?: { status?: number } })?.response?.status === 404
      ? 'missing'
      : partnershipQuery.isLoading
      ? 'loading'
      : draft
      ? 'ready'
      : 'loading';

  /* ----- "create page" state ----- */
  if (partnershipStatus === 'missing') {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <button type="button" className="link" onClick={() => navigate(`/campaigns/${id}`)} style={{ fontSize: 12 }}>
            ← Back to campaign
          </button>
        </div>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>No partnership page yet</h2>
          <p style={{ color: 'var(--ink-3)', maxWidth: 520, margin: '0 auto 18px' }}>
            Create a partner-facing page for "{campaign.name}". It'll pre-fill from the campaign
            (title, dates, brands) — you can then tune the packs, add-ons and contacts.
          </p>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                await createMutation.mutateAsync(id);
                toast.push('Partnership page created — start editing below.');
              } catch {
                toast.push('Could not create the page');
              }
            }}
            disabled={createMutation.isPending}
          >
            <Icon name="plus" size={13} /> Create partnership page
          </Button>
        </div>
      </div>
    );
  }

  if (partnershipStatus === 'loading') {
    return <div className="card" style={{ padding: 32 }}>Loading partnership…</div>;
  }
  if (!draft) {
    return <div className="card" style={{ padding: 32 }}>Could not load the partnership page.</div>;
  }

  const patch = (next: Partial<Partnership>) => {
    setDraft({ ...draft, ...next });
    setDirty(true);
  };

  const setPreviewImage = (id: string, dataUrl?: string) => {
    const next = { ...(draft.preview_images || {}) };
    if (dataUrl) next[id] = dataUrl;
    else delete next[id];
    patch({ preview_images: next });
  };


  const translate = async () => {
    if (!id) return;
    setTranslating(true);
    try {
      const res = await axiosClient.post(`api/partnerships/${id}/translate/`);
      setDraft(res.data);
      setDirty(false);
      partnershipQuery.refetch();
      toast.push('Dutch and French versions are ready — check them under Translations.');
    } catch (err: unknown) {
      const d = (err as { response?: { data?: { detail?: string } } })?.response?.data;
      toast.push(d?.detail || 'Could not translate — try again.');
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    try {
      const { campaign: _campaign, campaign_details: _details, created_at: _c, updated_at: _u, ...payload } = draft;
      await updateMutation.mutateAsync(payload);
      toast.push('Saved.');
      setDirty(false);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      toast.push(detail ? `Could not save: ${JSON.stringify(detail)}` : 'Could not save');
    }
  };

  const togglePublish = async () => {
    try {
      await publishMutation.mutateAsync(!draft.published);
      toast.push(draft.published ? 'Unpublished — the public link now 404s.' : 'Published — the page is live.');
    } catch {
      toast.push('Could not toggle publish state');
    }
  };

  const sharePage = async () => {
    if (!draft.published) {
      toast.push('Publish the page first — it 404s while in draft.');
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.push('Share link copied to clipboard.');
    } catch {
      toast.push(`Copy failed — link: ${publicUrl}`);
    }
  };

  const deletePage = async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.push('Partnership page deleted.');
      navigate(`/campaigns/${id}`);
    } catch {
      toast.push('Could not delete the page');
    }
    setConfirmDelete(false);
  };


  return (
    <div className="pp pp-wide">
      <style>{PP_STYLES}</style>
      {/* Header strip */}
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="link" onClick={() => navigate(`/campaigns/${id}`)} style={{ fontSize: 12 }}>
          ← Back to campaign
        </button>
      </div>
      <div className="card" style={{ padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="page-eyebrow">Campaign partnership page</div>
            <h1 className="page-title" style={{ margin: 0 }}>{campaign.name}</h1>
            <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
              Edits below show on the partner-facing page at{' '}
              <code style={{ background: 'var(--bg-subtle)', padding: '1px 6px', borderRadius: 4 }}>
                /partnerships/{draft.slug}
              </code>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                background: draft.published ? 'var(--seasonal-bg)' : 'var(--bg-subtle)',
                color: draft.published ? 'var(--seasonal)' : 'var(--ink-4)',
              }}
            >
              {draft.published ? 'Published' : 'Draft'}
            </span>
            <Button variant="ghost" onClick={togglePublish} disabled={publishMutation.isPending}>
              {draft.published ? 'Unpublish' : 'Publish'}
            </Button>
            <Button variant="ghost" onClick={sharePage}>
              <Icon name="link" size={13} /> Share page
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} style={{ color: 'var(--warn)' }}>
              <Icon name="x" size={13} /> Delete page
            </Button>
            <Button variant="primary" onClick={save} disabled={!dirty || updateMutation.isPending}>
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs: Setup (page content) vs. Follow-up (partner tracking) */}
      <div
        className="m-wrap"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          borderBottom: '1px solid var(--border)',
        }}
      >
      <div
        role="tablist"
        className="m-scroll"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: 'none',
        }}
      >
        {([
          { id: 'setup', label: 'Page' },
          ...(draft.uses_packs ? [] : [{ id: 'placements' as const, label: 'Placements' }]),
          {
            id: 'follow-up',
            label: 'Outreach',
            count: followUpRows.length || undefined,
          },
          // Results is hidden on booking pages until we have results to
          // log (Doreen, 2026-10-27); old pack pages keep it.
          ...(draft.uses_packs ? [{ id: 'results' as const, label: 'Results' }] : []),
          { id: 'billing', label: 'Billing' },
        ] as const).map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setActiveTab(t.id)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: 13.5,
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--ink)' : 'var(--ink-4)',
                borderBottom: active ? '2px solid var(--ink)' : '2px solid transparent',
                marginBottom: -1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {t.label}
              {'count' in t && t.count != null && (
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '1px 7px',
                  borderRadius: 999,
                  background: 'var(--bg-subtle)',
                  color: 'var(--ink-3)',
                }}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
        <div style={{ paddingBottom: 8 }}>
          <HowItWorksLink
            section={
              activeTab === 'setup' ? 'partnership-setup'
              : activeTab === 'placements' ? 'partnership-placements'
              : activeTab === 'follow-up' ? 'outreach'
              : activeTab === 'results' ? 'results'
              : 'billing'
            }
          />
        </div>
      </div>

      {activeTab === 'setup' && (<>

      {/* Page settings — the switches that change how the page behaves,
          always visible on top (no need to open a section to find them). */}
      <div className="pp-settings">
        <div className="tile">
          <div className="k">Sign-ups</div>
          <Seg
            value={draft.closed_for_opt_in ? 'closed' : 'open'}
            options={[{ v: 'open', l: 'Open', cls: 'yes' }, { v: 'closed', l: 'Closed', cls: 'no' }]}
            onChange={(v) => patch({ closed_for_opt_in: v === 'closed' })}
          />
          <div className="hint">
            {draft.closed_for_opt_in
              ? 'The page still opens, but brands can no longer sign up.'
              : 'Brands can book their slots on the page.'}
          </div>
        </div>
        <div className="tile">
          <div className="k">Minimum discount</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Seg
              value={draft.min_discount_pct != null ? 'on' : 'off'}
              options={[{ v: 'off', l: 'Off' }, { v: 'on', l: 'On' }]}
              onChange={(v) => patch({ min_discount_pct: v === 'on' ? (draft.min_discount_pct ?? '15') : null })}
            />
            {draft.min_discount_pct != null && (
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={90}
                  value={String(Number(draft.min_discount_pct))}
                  onChange={(e) => patch({ min_discount_pct: e.target.value === '' ? '0' : e.target.value })}
                  style={{ width: 76, paddingRight: 24 }}
                  aria-label="Minimum discount in %"
                />
                <span style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--ink-4)', pointerEvents: 'none' }}>%</span>
              </span>
            )}
          </div>
          <div className="hint">
            {draft.min_discount_pct != null
              ? `Brands need to give shoppers at least ${Number(draft.min_discount_pct)}% to take part.`
              : 'No minimum asked (e.g. Black Friday).'}
          </div>
        </div>
        <div className="tile">
          <div className="k">Book by</div>
          <input
            className="input"
            type="date"
            value={draft.confirmation_deadline ?? ''}
            onChange={(e) => patch({ confirmation_deadline: e.target.value || null })}
            style={{ maxWidth: 170 }}
          />
          <div className="hint">Optional — shown on the page and in the outreach email.</div>
        </div>
        <div className="tile">
          <div className="k">Campaign brands on the page</div>
          <Seg
            value={draft.show_brands ? 'show' : 'hide'}
            options={[{ v: 'show', l: 'Show' }, { v: 'hide', l: 'Hide' }]}
            onChange={(v) => patch({ show_brands: v === 'show' })}
          />
          <div className="hint">Lists the campaign's brands under "Campaign partnership proposal for".</div>
        </div>
        {draft.uses_packs && (
          <div className="tile">
            <div className="k">Packs</div>
            <Seg
              value="on"
              options={[{ v: 'on', l: 'Used on this page' }, { v: 'off', l: 'Switch off' }]}
              onChange={(v) => { if (v === 'off') patch({ uses_packs: false }); }}
            />
            <div className="hint">This page still sells packs and add-ons, because brands signed up with them. Switching off hides them for good.</div>
          </div>
        )}
        <div className="tile">
          <div className="k">Reply-to</div>
          <div className="chips">
            {contacts.map((c) => {
              const on = draft.contacts.some((x) => x.email === c.email);
              return (
                <button
                  key={c.email}
                  type="button"
                  aria-pressed={on}
                  title={c.email}
                  onClick={() => {
                    const next = on
                      ? draft.contacts.filter((x) => x.email !== c.email)
                      : [...draft.contacts, { ...c }];
                    patch({ contacts: next.length ? next : [{ ...c }] });
                  }}
                >
                  {on && <Icon name="check" size={10} />}
                  {c.name}
                </button>
              );
            })}
          </div>
          <div className="hint">Who brands answer to. At least one.</div>
        </div>
      </div>

      <PPSection
        k="text"
        summary={`/partnerships/${draft.slug}${draft.stats.length ? ` · ${draft.stats.length} number${draft.stats.length === 1 ? '' : 's'}` : ''}`}
      >
        <Group title="Link and title">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Public URL slug</label>
              <input
                className="input"
                value={draft.slug}
                onChange={(e) => patch({ slug: slugify(e.target.value) })}
              />
            </div>
            <div>
              <label style={labelStyle}>Eyebrow (small label above the title)</label>
              <input
                className="input"
                value={draft.eyebrow}
                onChange={(e) => patch({ eyebrow: e.target.value })}
              />
            </div>
          </div>
        </Group>
        <Group title="Intro">
          <div style={{ marginTop: 14 }}>
            <label style={labelStyle}>Intro — a few short paragraphs about the campaign (starts from the campaign description)</label>
            <textarea
              className="input"
              rows={4}
              value={draft.intro}
              onChange={(e) => patch({ intro: e.target.value })}
              placeholder="The campaign's description — paragraphs separated by a blank line."
            />
            <AiTextSuggest campaignId={id} field="intro" current={draft.intro} onUse={(t) => patch({ intro: t })} />
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={labelStyle}>Page intro — short line under the title</label>
            <textarea
              className="input"
              rows={3}
              value={draft.page_intro}
              onChange={(e) => patch({ page_intro: e.target.value })}
              placeholder="One or two sentences: what this page is for — pick placements, tick the days, sign up."
            />
            <AiTextSuggest campaignId={id} field="page_intro" current={draft.page_intro} onUse={(t) => patch({ page_intro: t })} />
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={labelStyle}>Day picker note (optional) — short line next to the days on the booking page</label>
            <textarea
              className="input"
              rows={2}
              value={draft.days_note ?? ''}
              onChange={(e) => patch({ days_note: e.target.value })}
              placeholder="E.g. why the weekdays are worth booking too."
            />
          </div>
        </Group>
        <Group title="By the numbers" sub="Optional numbers strip right under the intro: big yellow figures on a dark band.">
          <label style={labelStyle}>Heading (optional)</label>
          <input
            className="input"
            value={draft.stats_title ?? ''}
            onChange={(e) => patch({ stats_title: e.target.value })}
            placeholder="Black Week 2025 in numbers"
            style={{ marginBottom: 12 }}
          />
          <ListEditor
            items={draft.stats}
            onChange={(stats) => patch({ stats })}
            blank={() => ({ value: '', label: '' } as PartnershipStat)}
            addLabel="Add stat"
            renderItem={(s, _idx, update) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                  <input className="input" placeholder="240k" value={s.value} onChange={(e) => update({ value: e.target.value })} />
                  <input className="input" placeholder="Newsletter recipients" value={s.label} onChange={(e) => update({ label: e.target.value })} />
                </div>
                <input className="input" placeholder="Note (optional — small line under the label)" value={s.note ?? ''} onChange={(e) => update({ note: e.target.value || undefined })} />
              </div>
            )}
          />
        </Group>
      </PPSection>

      {draft.uses_packs && (
      <PPSection
        k="offer"
        summary={draft.uses_packs
          ? `${draft.features.length} feature${draft.features.length === 1 ? '' : 's'} · ${draft.packs.length} pack${draft.packs.length === 1 ? '' : 's'} · ${draft.addons.length} add-on${draft.addons.length === 1 ? '' : 's'}`
          : `${draft.features.length} placement${draft.features.length === 1 ? '' : 's'}`}
      >
        <Group
          title={draft.uses_packs ? 'Features' : 'Placements'}
          sub={draft.uses_packs
            ? 'The perks this campaign offers. Add standard ones from the list below or write your own.'
            : 'What brands can sign up for. These move to their own Placements tab (with price, slots and the website map) in the next step.'}
        >
          <ListEditor
            items={draft.features}
            onChange={(features) => patch({ features })}
            blank={() => ({ id: `feat-${Date.now()}`, label: '' } as PartnershipFeature)}
            addLabel="Add a custom feature"
            emptyLabel="No features yet — add the standard ones or write your own."
            renderItem={(f, _idx, update) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input
                  className="input"
                  placeholder="Feature name"
                  value={f.label}
                  onChange={(e) => update({ label: e.target.value })}
                  style={primaryInputStyle}
                />
                <input
                  className="input"
                  placeholder="hint (optional)"
                  value={f.hint ?? ''}
                  onChange={(e) => update({ hint: e.target.value || undefined })}
                  style={{ color: 'var(--ink-3)' }}
                />
                <AssetMappingPicker
                  type={f.produces_asset_type}
                  grouping={f.asset_grouping}
                  onChange={(patch) => update(patch)}
                />
              </div>
            )}
          />
          {catalog && (
            <AddFromCatalog
              catalog={catalog.features}
              present={draft.features}
              label="+ Add a standard feature…"
              describe={(f) => f.label}
              onAdd={(f) => patch({ features: [...draft.features, f] })}
            />
          )}
        </Group>
        {draft.uses_packs && (<>
        <Group title="Visibility packs" sub="Each pack's price and which features it includes.">
          <ListEditor
            items={draft.packs}
            onChange={(packs) => patch({ packs })}
            blank={() => ({ id: `pack-${Date.now()}`, name: '', price: 0, includes: [] } as PartnershipPack)}
            addLabel="Add a custom pack"
            emptyLabel="No packs yet — add the standard tiers or build your own."
            renderItem={(p, idx, update) => {
              // Ladder check: a pack should include everything every cheaper pack does.
              const cheaper = draft.packs.filter((q) => q.id !== p.id && q.price < p.price);
              const missing = new Set<string>();
              cheaper.forEach((q) => q.includes.forEach((fid) => {
                if (!p.includes.includes(fid)) missing.add(fid);
              }));
              const missingLabels = draft.features
                .filter((f) => missing.has(f.id))
                .map((f) => f.label);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 8, alignItems: 'end' }}>
                    <input className="input" placeholder="Pack name" value={p.name} onChange={(e) => update({ name: e.target.value })} style={primaryInputStyle} />
                    <div>
                      <span style={{ ...labelStyle, marginBottom: 2 }}>Price (€)</span>
                      <input className="input" type="number" placeholder="0" value={p.price} onChange={(e) => update({ price: Number(e.target.value) || 0 })} style={{ width: '100%' }} />
                    </div>
                  </div>
                  <textarea className="input" rows={2} placeholder="Blurb (one line under the price)" value={p.blurb ?? ''} onChange={(e) => update({ blurb: e.target.value || undefined })} />

                  {/* — Highlighting & messaging — */}
                  <SubsectionLabel>Highlighting &amp; messaging</SubsectionLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                      <input type="checkbox" checked={!!p.highlight} onChange={(e) => update({ highlight: e.target.checked || undefined })} />
                      Highlight as the "Best value" pack
                    </label>
                    <input className="input" placeholder="Scarcity note (optional)" value={p.scarcity_note ?? ''} onChange={(e) => update({ scarcity_note: e.target.value || undefined })} />
                  </div>

                  {/* — Availability — */}
                  <SubsectionLabel>Availability</SubsectionLabel>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                    <input type="checkbox" checked={!!p.sold_out} onChange={(e) => update({ sold_out: e.target.checked || undefined })} />
                    Mark as "no longer available" (visible but not selectable)
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                    <label style={{ flexShrink: 0 }}>Capacity (optional):</label>
                    <input
                      type="number" min={0} step={1}
                      className="input" style={{ maxWidth: 100 }}
                      value={p.capacity ?? ''}
                      placeholder="∞"
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        update({ capacity: Number.isFinite(n) && n > 0 ? n : undefined });
                      }}
                    />
                    <span style={{ color: 'var(--ink-4)' }}>
                      auto-flags as sold-out once N brands are <em>confirmed</em> on this pack.
                    </span>
                  </div>

                  {/* — Included features — */}
                  <SubsectionLabel>Included features</SubsectionLabel>
                  <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {draft.features.length === 0 && (
                        <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>Add features in Step 1 first.</span>
                      )}
                      {draft.features.map((f) => {
                        const on = p.includes.includes(f.id);
                        return (
                          <label key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={(e) =>
                                update({
                                  includes: e.target.checked
                                    ? [...p.includes, f.id]
                                    : p.includes.filter((x) => x !== f.id),
                                })
                              }
                            />
                            {f.label || <em style={{ color: 'var(--ink-4)' }}>(unnamed feature)</em>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {missingLabels.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--warn)', background: 'var(--warn-bg)', padding: '6px 10px', borderRadius: 'var(--r-md)' }}>
                      Not a clean step up — a cheaper pack includes: {missingLabels.join(', ')}.
                      Partners expect each tier to contain everything below it.
                    </div>
                  )}
                  {idx === 0 && draft.packs.length > 1 && (
                    <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                      Tip: order packs cheapest → most expensive (use ↑↓).
                    </div>
                  )}
                </div>
              );
            }}
          />
          {catalog && (
            <AddFromCatalog
              catalog={catalog.packs}
              present={draft.packs}
              label="+ Add a standard pack…"
              describe={(p) => `${p.name} — €${p.price}`}
              onAdd={(p) => patch({ packs: [...draft.packs, p] })}
            />
          )}
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-4)' }}>
            With any pack the partner gets its features for the whole campaign — no rotation
            slot to pick. Limited add-ons carry their own "days within the campaign" below.
          </div>
        </Group>
        <Group title="Add-ons" sub="Extras on top of any pack: price, days within the campaign, and which packs they're offered with.">
          <ListEditor
            items={draft.addons}
            onChange={(addons) => patch({ addons })}
            blank={() => ({ id: `addon-${Date.now()}`, label: '', description: '', price: 0 } as PartnershipAddon)}
            addLabel="Add a custom add-on"
            emptyLabel="No add-ons yet — add the standard ones or write your own."
            renderItem={(a, _idx, update) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px', gap: 8, alignItems: 'end' }}>
                  <input className="input" placeholder="Add-on name" value={a.label} onChange={(e) => update({ label: e.target.value })} style={primaryInputStyle} />
                  <div>
                    <span style={{ ...labelStyle, marginBottom: 2 }}>Price (€)</span>
                    <input className="input" type="number" placeholder="0" value={a.price} onChange={(e) => update({ price: Number(e.target.value) || 0 })} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <span style={{ ...labelStyle, marginBottom: 2 }}>Days</span>
                    <input className="input" type="number" placeholder="—" value={a.days ?? ''} onChange={(e) => update({ days: e.target.value ? Number(e.target.value) : undefined })} style={{ width: '100%' }} />
                  </div>
                </div>
                <textarea className="input" rows={2} placeholder="Description" value={a.description ?? ''} onChange={(e) => update({ description: e.target.value })} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                  <input className="input" placeholder="Price note (e.g. Free / On request)" value={a.price_note ?? ''} onChange={(e) => update({ price_note: e.target.value || undefined })} />
                  <input className="input" placeholder="Scarcity note (optional)" value={a.scarcity_note ?? ''} onChange={(e) => update({ scarcity_note: e.target.value || undefined })} />
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!a.recommended} onChange={(e) => update({ recommended: e.target.checked || undefined })} />
                    Mark as recommended
                  </label>
                </div>
                <div>
                  <div style={{ ...labelStyle, marginBottom: 6 }}>Available with (leave all unticked = every pack)</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {draft.packs.map((p) => {
                      const on = (a.available_for ?? []).includes(p.id);
                      return (
                        <label key={p.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => {
                              const cur = a.available_for ?? [];
                              const next = e.target.checked ? [...cur, p.id] : cur.filter((x) => x !== p.id);
                              update({ available_for: next.length ? next : undefined });
                            }}
                          />
                          {p.name || p.id}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                  <input type="checkbox" checked={!!a.campaign_specific} onChange={(e) => update({ campaign_specific: e.target.checked || undefined })} />
                  Campaign-specific extra (shows a "Campaign extra" tag)
                </label>
                <AssetMappingPicker
                  type={a.produces_asset_type}
                  grouping={a.asset_grouping}
                  onChange={(patch) => update(patch)}
                />
              </div>
            )}
          />
          {catalog && (
            <AddFromCatalog
              catalog={catalog.addons}
              present={draft.addons}
              label="+ Add a standard add-on…"
              describe={(a) => `${a.label} — ${a.price > 0 ? `€${a.price}` : a.price_note || 'Free'}`}
              onAdd={(a) => patch({ addons: [...draft.addons, a] })}
            />
          )}
        </Group>
        </>)}
      </PPSection>
      )}

      {draft.uses_packs && (
      <PPSection
        k="pictures"
        summary={(() => {
          const ids = [...draft.features, ...draft.addons].map((x) => x.id);
          const withImg = ids.filter((i) => draft.preview_images?.[i]).length;
          return `${withImg} of ${ids.length} with a picture`;
        })()}
      >
        <div className="sec-sub">One tile per feature and add-on — the name and explanation come from "What brands can buy". Upload a picture (optional; without one the page shows a clean placeholder). Add extra tiles below for anything else.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            ...draft.features.map((f) => ({ id: f.id, label: f.label, explain: f.hint })),
            ...draft.addons.map((a) => ({ id: a.id, label: a.label, explain: a.description })),
          ].map((it) => {
            const img = draft.preview_images?.[it.id];
            const hidden = !!(draft.preview_hidden ?? {})[it.id];
            return (
              <div
                key={it.id}
                style={{
                  display: 'flex', gap: 14, alignItems: 'flex-start', padding: 12,
                  border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                  background: 'var(--bg-elevated)',
                  opacity: hidden ? 0.55 : 1,
                }}
              >
                <div
                  style={{
                    width: 120, flexShrink: 0, aspectRatio: '4 / 3',
                    background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {img ? (
                    <img src={img} alt={it.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>No image</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{it.label || '(unnamed)'}</div>
                  {it.explain && (
                    <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>{it.explain}</div>
                  )}
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                    <label className="link" style={{ cursor: 'pointer', fontSize: 13 }}>
                      {img ? 'Replace image' : 'Upload image'}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          try {
                            setPreviewImage(it.id, await compressImage(file));
                          } catch {
                            toast.push('Could not read that image');
                          }
                        }}
                      />
                    </label>
                    {img && (
                      <button type="button" className="link" style={{ color: 'var(--warn)', fontSize: 13 }} onClick={() => setPreviewImage(it.id, undefined)}>
                        Remove
                      </button>
                    )}
                    <label
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        marginLeft: 'auto', fontSize: 12.5, color: 'var(--ink-3)',
                        cursor: 'pointer', userSelect: 'none',
                      }}
                      title="Uncheck to leave this tile off the public partnership page."
                    >
                      <input
                        type="checkbox"
                        checked={!hidden}
                        onChange={(e) => {
                          const next = { ...(draft.preview_hidden ?? {}) };
                          if (e.target.checked) delete next[it.id];
                          else next[it.id] = true;
                          patch({ preview_hidden: next });
                        }}
                      />
                      Show on partner page
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
          {draft.features.length === 0 && draft.addons.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>
              Add features / add-ons in the earlier steps first.
            </div>
          )}
        </div>

        <div style={{ ...labelStyle, marginTop: 20 }}>Extra tiles (not tied to a feature or add-on)</div>
        <ListEditor
          items={draft.extra_previews}
          onChange={(extra_previews) => patch({ extra_previews })}
          blank={() => ({ id: `extra-${Date.now()}`, label: '', shape: 'landscape' } as PartnershipPreview)}
          addLabel="Add an extra tile"
          emptyLabel="No extra tiles — add one only to show something beyond the features & add-ons."
          renderItem={(pv, _idx, update) => (
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 120, flexShrink: 0, aspectRatio: '4 / 3',
                  background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {pv.image ? (
                  <img src={pv.image} alt={pv.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>No image</span>
                )}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input className="input" placeholder="Tile name" value={pv.label} onChange={(e) => update({ label: e.target.value })} style={primaryInputStyle} />
                <input className="input" placeholder="Caption (optional)" value={pv.caption ?? ''} onChange={(e) => update({ caption: e.target.value || undefined })} />
                <label className="link" style={{ cursor: 'pointer', fontSize: 13 }}>
                  {pv.image ? 'Replace image' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      try {
                        update({ image: await compressImage(file) });
                      } catch {
                        toast.push('Could not read that image');
                      }
                    }}
                  />
                </label>
                {pv.image && (
                  <button type="button" className="link" style={{ color: 'var(--warn)', fontSize: 13, alignSelf: 'flex-start' }} onClick={() => update({ image: undefined })}>
                    Remove image
                  </button>
                )}
              </div>
            </div>
          )}
        />
      </PPSection>
      )}

      <PPSection
        k="info"
        summary={[
          draft.brand_responsibilities.replace(/<[^>]*>/g, '').trim() && (draft.extra_section_title || 'Practical info'),
          (draft.faq ?? []).length ? `${(draft.faq ?? []).length} FAQ` : '',
          draft.participation_disclaimer.trim() ? 'sign-up terms' : '',
        ].filter(Boolean).join(' · ')}
      >
        <Group title="Practical info" sub="Anything brands should know about the campaign: what we need from them, timing, practicalities. Shows on the page as soon as there's text.">
          {draft.uses_packs && (
            <label className="check" style={{ marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={draft.show_extra_section}
                onChange={(e) => patch({ show_extra_section: e.target.checked })}
              />
              Show this section on the page
            </label>
          )}
          <label style={labelStyle}>Heading</label>
          <input
            className="input"
            value={draft.extra_section_title}
            onChange={(e) => patch({ extra_section_title: e.target.value })}
            placeholder="Good to know"
            style={{ ...primaryInputStyle, marginBottom: 10 }}
          />
          <label style={labelStyle}>Text (headings, bullets, links, bold)</label>
          <RichTextEditor
            value={draft.brand_responsibilities}
            onChange={(html) => patch(
              draft.uses_packs
                ? { brand_responsibilities: html }
                : { brand_responsibilities: html, show_extra_section: !!html.replace(/<[^>]*>/g, '').trim() },
            )}
            placeholder="What we need from you, and when…"
          />
        </Group>
        <Group title="FAQ" sub="Questions and answers near the bottom of the page.">
          <ListEditor
            items={draft.faq ?? []}
            onChange={(faq) => patch({ faq })}
            blank={() => ({ question: '', answer: '' })}
            addLabel="Add a question"
            renderItem={(item, _idx, update) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input
                  className="input"
                  placeholder="Question"
                  value={item.question}
                  onChange={(e) => update({ question: e.target.value })}
                  style={primaryInputStyle}
                />
                <textarea
                  className="input"
                  rows={2}
                  placeholder="Answer"
                  value={item.answer}
                  onChange={(e) => update({ answer: e.target.value })}
                />
              </div>
            )}
          />
        </Group>
        <Group
          title="Sign-up terms"
          sub={draft.uses_packs
            ? 'Small note next to the "Confirm my participation" button.'
            : 'Shown above the "Sign up" button. Brands tick "I agree" before they can sign up — signing up is final.'}
        >
          <textarea
            className="input"
            rows={4}
            value={draft.participation_disclaimer}
            onChange={(e) => patch({ participation_disclaimer: e.target.value })}
            placeholder="Signing up is final: your brand is included in the placements you selected…"
          />
        </Group>
        {draft.uses_packs && (
          <Group title="Texts of the pack layout" sub="Only on this page, because it still uses packs.">
            <label style={labelStyle}>Auto-include note · small card above the packs</label>
            <textarea
              className="input"
              rows={3}
              value={draft.auto_include_note}
              onChange={(e) => patch({ auto_include_note: e.target.value })}
            />
            <label style={{ ...labelStyle, marginTop: 12 }}>Opt-out text · line next to the reply button</label>
            <textarea
              className="input"
              rows={2}
              value={draft.opt_out_note}
              onChange={(e) => patch({ opt_out_note: e.target.value })}
            />
            <label style={{ ...labelStyle, marginTop: 12 }}>Settlement note · small text next to the total</label>
            <input
              className="input"
              value={draft.settlement_note}
              onChange={(e) => patch({ settlement_note: e.target.value })}
            />
          </Group>
        )}
      </PPSection>

      {!draft.uses_packs && (
        <PPSection
          k="lang"
          summary={draft.translation_status?.translated_at
            ? (draft.translation_status.outdated ? 'out of date — translate again' : 'NL · FR ready')
            : 'not translated yet'}
        >
          <TranslationsPanel
            draft={draft}
            patch={patch}
            campaignId={id}
            dirty={dirty}
            translating={translating}
            onTranslate={translate}
          />
        </PPSection>
      )}

      <PPSection k="emails" summary="Outreach and confirmation">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>
              Outreach email template{' '}
              <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>
                · used by the ✉ button on each brand row. Placeholders get replaced per-brand:
                {' '}<code>{'{first_name}'}</code>, <code>{'{brand}'}</code>, <code>{'{campaign}'}</code>,
                {' '}<code>{'{dates}'}</code>, <code>{'{deadline_line}'}</code>, <code>{'{link}'}</code>.
              </span>
            </label>
            <textarea
              className="input"
              rows={12}
              value={draft.outreach_email_template}
              onChange={(e) => patch({ outreach_email_template: e.target.value })}
              style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Confirmation email template{' '}
              <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>
                · used by the ✓ button on each brand row, once a brand has signed up. Placeholders:
                {' '}<code>{'{first_name}'}</code>, <code>{'{brand}'}</code>, <code>{'{campaign}'}</code>,
                {' '}<code>{'{slots}'}</code>, <code>{'{total}'}</code>, <code>{'{discount}'}</code>,
                {' '}<code>{'{dates}'}</code>.
              </span>
            </label>
            <textarea
              className="input"
              rows={10}
              value={draft.confirmation_email_template}
              onChange={(e) => patch({ confirmation_email_template: e.target.value })}
              style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
            />
          </div>
        </div>
      </PPSection>

      </>)}

      {activeTab === 'placements' && <PlacementsPanel campaignId={id} />}

      {activeTab === 'follow-up' && (
        <div className="card" style={{ padding: 20 }}>
          <div className="m-wrap" style={{ marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ fontSize: 15, margin: 0 }}>Partners</h3>
              <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
                Track who you contacted, who replied, and what they picked.
              </div>
            </div>
            <GenerateDraftPlacementsButton campaignId={id} rows={followUpRows} />
          </div>
          <PartnersTable partnership={draft} campaignId={id} />
        </div>
      )}

      {activeTab === 'results' && draft.uses_packs && (
        <ResultsPanel
          campaignId={id}
          campaignName={campaign?.name ?? ''}
          partnership={draft}
          rows={followUpRows}
        />
      )}

      {activeTab === 'billing' && (
        <BillingPanel
          partnership={draft}
          rows={followUpRows}
          campaignName={campaign?.name ?? ''}
          campaignId={id ?? ''}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={deletePage}
        title={`Delete the partnership page for "${campaign.name}"?`}
        description="The page and all its packs, add-ons and previews are removed. The campaign itself is untouched — you can always create a fresh partnership page later. This cannot be undone."
        confirmLabel="Delete page"
        danger
        busy={deleteMutation.isPending}
      />

      {/* Floating save bar — also reachable via Save at the top */}
      {dirty && (
        <div
          style={{
            position: 'sticky',
            bottom: 12,
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 16,
          }}
        >
          <div className="card" style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', boxShadow: 'var(--shadow-md)' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>You have unsaved changes.</span>
            <Button variant="ghost" onClick={() => { setDraft(partnershipQuery.data ?? null); setDirty(false); }}>Discard</Button>
            <Button variant="primary" onClick={save} disabled={updateMutation.isPending}>Save changes</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignPartnershipEditor;
